import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import Navbar from "./Navbar";
import AudioPlayer from "./AudioPlayer";
import { BookOpen, CheckCircle, Play, Volume2, Target, Lightbulb, Users, Clock, Award } from "lucide-react";

const lessons = {
beginner1: {
    title: "Hangul Basics - Korean Alphabet",
    difficulty: "Beginner",
    duration: "45 min",
    description: "Master the Korean alphabet from vowels to consonants",
    icon: BookOpen,
    content: {
      introduction: "Welcome to your first Korean lesson! Hangul (한글) is the Korean writing system, created in 1443 by King Sejong. It's considered one of the most scientific writing systems in the world.",
      
      sections: [
        {
          title: "Basic Consonants (자음)",
          content: "Korean has 14 basic consonants. Each represents a different sound:",
          items: [
            { korean: "ㄱ", roman: "g/k", english: "like 'g' in 'go' or 'k' in 'key'" },
            { korean: "ㄴ", roman: "n", english: "like 'n' in 'no'" },
            { korean: "ㄷ", roman: "d/t", english: "like 'd' in 'do' or 't' in 'to'" },
            { korean: "ㄹ", roman: "r/l", english: "like 'r' in 'run' or 'l' in 'love'" },
            { korean: "ㅁ", roman: "m", english: "like 'm' in 'mother'" },
            { korean: "ㅂ", roman: "b/p", english: "like 'b' in 'boy' or 'p' in 'pen'" },
            { korean: "ㅅ", roman: "s", english: "like 's' in 'sun'" },
            { korean: "ㅇ", roman: "ng", english: "like 'ng' in 'sing' (silent at start)" },
            { korean: "ㅈ", roman: "j", english: "like 'j' in 'jump'" },
            { korean: "ㅊ", roman: "ch", english: "like 'ch' in 'church'" },
            { korean: "ㅋ", roman: "k", english: "stronger 'k' sound" },
            { korean: "ㅌ", roman: "t", english: "stronger 't' sound" },
            { korean: "ㅍ", roman: "p", english: "stronger 'p' sound" },
            { korean: "ㅎ", roman: "h", english: "like 'h' in 'hello'" }
          ]
        },
        {
          title: "Basic Vowels (모음)",
          content: "Korean has 10 basic vowels. They form the foundation of Korean pronunciation:",
          items: [
            { korean: "ㅏ", roman: "a", english: "like 'a' in 'father'" },
            { korean: "ㅑ", roman: "ya", english: "like 'ya' in 'yard'" },
            { korean: "ㅓ", roman: "eo", english: "like 'u' in 'hut'" },
            { korean: "ㅕ", roman: "yeo", english: "like 'yo' in 'yogurt'" },
            { korean: "ㅗ", roman: "o", english: "like 'o' in 'go'" },
            { korean: "ㅛ", roman: "yo", english: "like 'yo' in 'yogurt'" },
            { korean: "ㅜ", roman: "u", english: "like 'oo' in 'moon'" },
            { korean: "ㅠ", roman: "yu", english: "like 'you'" },
            { korean: "ㅡ", roman: "eu", english: "like 'u' in 'pull'" },
            { korean: "ㅣ", roman: "i", english: "like 'ee' in 'see'" }
          ]
        },
        {
          title: "Syllable Formation",
          content: "Korean syllables are formed by combining consonants and vowels into blocks:",
          examples: [
            { korean: "가", roman: "ga", english: "consonant ㄱ + vowel ㅏ" },
            { korean: "나", roman: "na", english: "consonant ㄴ + vowel ㅏ" },
            { korean: "다", roman: "da", english: "consonant ㄷ + vowel ㅏ" },
            { korean: "라", roman: "ra", english: "consonant ㄹ + vowel ㅏ" },
            { korean: "마", roman: "ma", english: "consonant ㅁ + vowel ㅏ" }
          ]
        }
      ],
      
      practice: {
        title: "Practice Exercise",
        instructions: "Try reading these Korean syllables out loud:",
        words: [
          { korean: "가나다라마", roman: "ga-na-da-ra-ma", english: "Korean alphabet order" },
          { korean: "바사아자차", roman: "ba-sa-a-ja-cha", english: "More consonants" },
          { korean: "카타파하", roman: "ka-ta-pa-ha", english: "Final consonants" }
        ]
      },
      
      tips: [
        "Practice writing each character 10 times to build muscle memory",
        "Focus on the shape and stroke order of each character",
        "Listen to native pronunciation and repeat after them",
        "Start with basic syllables before moving to complex ones"
      ]
    }
  },
  
  beginner2: {
    title: "Essential Greetings",
    difficulty: "Beginner", 
    duration: "30 min",
    description: "Learn polite greetings and basic conversation starters",
    icon: Users,
    content: {
      introduction: "Greetings are the foundation of Korean communication. Korean culture places great importance on politeness and respect, so using the right greeting for the right situation is crucial.",
      
      sections: [
        {
          title: "Basic Greetings (표준 인사말)",
          content: "These are the most common greetings you'll use daily:",
          items: [
            { korean: "안녕하세요", roman: "an-nyeong-ha-se-yo", english: "Hello (formal)", usage: "Use with strangers, elders, or in formal situations" },
            { korean: "안녕히 가세요", roman: "an-nyeong-hi ga-se-yo", english: "Goodbye (they leave)", usage: "When someone else is leaving" },
            { korean: "안녕히 계세요", roman: "an-nyeong-hi gye-se-yo", english: "Goodbye (you leave)", usage: "When you are leaving" },
            { korean: "감사합니다", roman: "gam-sa-ham-ni-da", english: "Thank you (formal)", usage: "Most common way to say thanks" },
            { korean: "죄송합니다", roman: "joe-song-ham-ni-da", english: "Sorry (formal)", usage: "For apologies or getting attention" },
            { korean: "처음 뵙겠습니다", roman: "cheo-eum boep-get-sseum-ni-da", english: "Nice to meet you", usage: "When meeting someone for the first time" },
            { korean: "네", roman: "ne", english: "Yes", usage: "Polite way to say yes" },
            { korean: "아니요", roman: "a-ni-yo", english: "No", usage: "Polite way to say no" }
          ]
        },
        {
          title: "Time-Specific Greetings",
          content: "Greetings that change based on the time of day:",
          items: [
            { korean: "좋은 아침입니다", roman: "jo-eun a-chim-im-ni-da", english: "Good morning", usage: "Morning greeting" },
            { korean: "안녕히 주무세요", roman: "an-nyeong-hi ju-mu-se-yo", english: "Good night", usage: "Before going to sleep" },
            { korean: "잘 지내세요?", roman: "jal ji-nae-se-yo?", english: "How are you?", usage: "Asking about someone's wellbeing" }
          ]
        },
        {
          title: "Casual Greetings (반말)",
          content: "Use these only with close friends or people younger than you:",
          items: [
            { korean: "안녕", roman: "an-nyeong", english: "Hi/Bye (casual)", usage: "With close friends" },
            { korean: "고마워", roman: "go-ma-wo", english: "Thanks (casual)", usage: "With friends" },
            { korean: "미안해", roman: "mi-an-hae", english: "Sorry (casual)", usage: "With friends" }
          ]
        }
      ],
      
      practice: {
        title: "Practice Scenarios",
        instructions: "What would you say in these situations?",
        scenarios: [
          { situation: "Meeting someone for the first time", answer: "처음 뵙겠습니다" },
          { situation: "Someone helped you", answer: "감사합니다" },
          { situation: "You need to get someone's attention", answer: "죄송합니다" },
          { situation: "Saying goodbye when you're leaving", answer: "안녕히 계세요" }
        ]
      },
      
      tips: [
        "Always bow slightly when greeting someone",
        "Use formal language (-세요, -습니다) unless you're very close",
        "Make eye contact when greeting",
        "Practice the pronunciation until it feels natural"
      ]
    }
  },
  
  beginner3: {
    title: "Self-Introduction",
    difficulty: "Beginner",
    duration: "35 min", 
    description: "Introduce yourself confidently in Korean",
    icon: Target,
    content: {
      introduction: "Learning to introduce yourself is essential for building relationships in Korea. This lesson covers the basic structure and polite forms for self-introduction.",
      
      sections: [
        {
          title: "Basic Introduction Phrases",
          content: "Essential phrases for introducing yourself:",
          items: [
            { korean: "제 이름은 ... 입니다", roman: "je i-reum-eun ... im-ni-da", english: "My name is ...", usage: "Standard way to introduce your name" },
            { korean: "저는 ... 입니다", roman: "jeo-neun ... im-ni-da", english: "I am ...", usage: "General introduction phrase" },
            { korean: "만나서 반갑습니다", roman: "man-na-seo ban-gap-seum-ni-da", english: "Nice to meet you", usage: "After introducing yourself" },
            { korean: "저는 ... 사람입니다", roman: "jeo-neun ... sa-ram-im-ni-da", english: "I am from ...", usage: "Introducing your nationality" }
          ]
        },
        {
          title: "Personal Information",
          content: "Common information people share when introducing themselves:",
          items: [
            { korean: "저는 학생입니다", roman: "jeo-neun hak-saeng-im-ni-da", english: "I am a student", usage: "If you're a student" },
            { korean: "저는 회사원입니다", roman: "jeo-neun hoe-sa-won-im-ni-da", english: "I am an office worker", usage: "If you work in an office" },
            { korean: "저는 한국어를 배우고 있어요", roman: "jeo-neun han-gu-geo-reul bae-u-go is-seo-yo", english: "I am learning Korean", usage: "Sharing your learning goal" },
            { korean: "저는 컴퓨터 공학을 전공해요", roman: "jeo-neun keom-pyu-teo gong-hak-eul jeon-gong-hae-yo", english: "I major in Computer Science", usage: "Sharing your field of study" }
          ]
        },
        {
          title: "Grammar Points",
          content: "Important grammar concepts for introductions:",
          items: [
            { korean: "저는", roman: "jeo-neun", english: "I (topic marker)", usage: "Humble form of 'I' + topic particle" },
            { korean: "입니다", roman: "im-ni-da", english: "is/am/are (formal)", usage: "Formal ending for 'to be'" },
            { korean: "이에요/예요", roman: "i-e-yo/ye-yo", english: "is/am/are (polite)", usage: "Polite ending for 'to be'" },
            { korean: "을/를", roman: "eul/reul", english: "object particle", usage: "Marks the object of a sentence" }
          ]
        }
      ],
      
      practice: {
        title: "Complete Introduction Practice",
        instructions: "Practice this complete self-introduction:",
        example: {
          korean: "안녕하세요. 제 이름은 케빈입니다. 저는 미국 사람입니다. 저는 컴퓨터 공학을 전공하고 있어요. 한국어를 배우고 있어요. 만나서 반갑습니다.",
          roman: "An-nyeong-ha-se-yo. Je i-reum-eun Ke-bin-im-ni-da. Jeo-neun Mi-guk sa-ram-im-ni-da. Jeo-neun keom-pyu-teo gong-hak-eul jeon-gong-ha-go is-seo-yo. Han-gu-geo-reul bae-u-go is-seo-yo. Man-na-seo ban-gap-seum-ni-da.",
          english: "Hello. My name is Kevin. I am American. I major in Computer Science. I am learning Korean. Nice to meet you."
        }
      },
      
      tips: [
        "Practice your introduction until it flows naturally",
        "Speak slowly and clearly when introducing yourself",
        "Make eye contact and smile",
        "Bow slightly when meeting someone for the first time"
      ]
    }
  },
  
  intermediate2: {
    title: "Numbers and Counting",
    difficulty: "Intermediate",
    duration: "40 min",
    description: "Master Korean numbers and counting systems",
    icon: Target,
    content: {
      introduction: "Korean has two different number systems: Sino-Korean (based on Chinese) and Native Korean. Understanding when to use each system is crucial for proper Korean communication.",
      
      sections: [
        {
          title: "Sino-Korean Numbers (일, 이, 삼...)",
          content: "Used for dates, money, phone numbers, and mathematical operations:",
          items: [
            { korean: "일", roman: "il", english: "1", usage: "Dates, money, phone numbers" },
            { korean: "이", roman: "i", english: "2", usage: "Dates, money, phone numbers" },
            { korean: "삼", roman: "sam", english: "3", usage: "Dates, money, phone numbers" },
            { korean: "사", roman: "sa", english: "4", usage: "Dates, money, phone numbers" },
            { korean: "오", roman: "o", english: "5", usage: "Dates, money, phone numbers" },
            { korean: "육", roman: "yuk", english: "6", usage: "Dates, money, phone numbers" },
            { korean: "칠", roman: "chil", english: "7", usage: "Dates, money, phone numbers" },
            { korean: "팔", roman: "pal", english: "8", usage: "Dates, money, phone numbers" },
            { korean: "구", roman: "gu", english: "9", usage: "Dates, money, phone numbers" },
            { korean: "십", roman: "sip", english: "10", usage: "Dates, money, phone numbers" }
          ]
        },
        {
          title: "Native Korean Numbers (하나, 둘, 셋...)",
          content: "Used for counting objects, age, and general counting:",
          items: [
            { korean: "하나", roman: "ha-na", english: "1", usage: "Counting objects, age" },
            { korean: "둘", roman: "dul", english: "2", usage: "Counting objects, age" },
            { korean: "셋", roman: "set", english: "3", usage: "Counting objects, age" },
            { korean: "넷", roman: "net", english: "4", usage: "Counting objects, age" },
            { korean: "다섯", roman: "da-seot", english: "5", usage: "Counting objects, age" },
            { korean: "여섯", roman: "yeo-seot", english: "6", usage: "Counting objects, age" },
            { korean: "일곱", roman: "il-gop", english: "7", usage: "Counting objects, age" },
            { korean: "여덟", roman: "yeo-deol", english: "8", usage: "Counting objects, age" },
            { korean: "아홉", roman: "a-hop", english: "9", usage: "Counting objects, age" },
            { korean: "열", roman: "yeol", english: "10", usage: "Counting objects, age" }
          ]
        },
        {
          title: "Counting Objects",
          content: "Korean uses specific counters for different types of objects:",
          examples: [
            { korean: "사과 한 개", roman: "sa-gwa han gae", english: "one apple", usage: "개 = general counter for objects" },
            { korean: "책 두 권", roman: "chaek du gwon", english: "two books", usage: "권 = counter for books" },
            { korean: "사람 세 명", roman: "sa-ram se myeong", english: "three people", usage: "명 = counter for people" },
            { korean: "컵 네 개", roman: "keop ne gae", english: "four cups", usage: "개 = counter for cups" },
            { korean: "차 다섯 대", roman: "cha da-seot dae", english: "five cars", usage: "대 = counter for vehicles" }
          ]
        }
      ],
      
      practice: {
        title: "Number Practice",
        instructions: "Practice reading these numbers and situations:",
        scenarios: [
          { situation: "Your phone number is 010-1234-5678", answer: "공일공-일이삼사-오육칠팔" },
          { situation: "You are 25 years old", answer: "스물다섯 살" },
          { situation: "Today is March 15th", answer: "삼월 십오일" },
          { situation: "The price is 5,000 won", answer: "오천 원" }
        ]
      },
      
      tips: [
        "Use Sino-Korean for dates, money, phone numbers, and math",
        "Use Native Korean for counting objects and stating age",
        "Practice both systems until they become natural",
        "Remember that some numbers change form when used with counters"
      ]
    }
  },
  
  intermediate3: {
    title: "Family and Relationships",
    difficulty: "Intermediate",
    duration: "45 min",
    description: "Learn Korean family terms and relationship vocabulary",
    icon: Users,
    content: {
      introduction: "Korean family relationships are complex and hierarchical. Understanding these terms is essential for Korean culture and communication.",
      
      sections: [
        {
          title: "Immediate Family",
          content: "Basic family members:",
          items: [
            { korean: "아버지", roman: "a-beo-ji", english: "father", usage: "Formal way to say father" },
            { korean: "어머니", roman: "eo-meo-ni", english: "mother", usage: "Formal way to say mother" },
            { korean: "아빠", roman: "a-ppa", english: "dad", usage: "Casual way to say father" },
            { korean: "엄마", roman: "eom-ma", english: "mom", usage: "Casual way to say mother" },
            { korean: "형", roman: "hyeong", english: "older brother (male speaking)", usage: "When a male talks about his older brother" },
            { korean: "누나", roman: "nu-na", english: "older sister (male speaking)", usage: "When a male talks about his older sister" },
            { korean: "오빠", roman: "o-ppa", english: "older brother (female speaking)", usage: "When a female talks about her older brother" },
            { korean: "언니", roman: "eon-ni", english: "older sister (female speaking)", usage: "When a female talks about her older sister" },
            { korean: "동생", roman: "dong-saeng", english: "younger sibling", usage: "General term for younger brother or sister" }
          ]
        },
        {
          title: "Extended Family",
          content: "Grandparents, aunts, uncles, and cousins:",
          items: [
            { korean: "할아버지", roman: "hal-a-beo-ji", english: "grandfather", usage: "Father's father" },
            { korean: "할머니", roman: "hal-meo-ni", english: "grandmother", usage: "Father's mother" },
            { korean: "외할아버지", roman: "oe-hal-a-beo-ji", english: "maternal grandfather", usage: "Mother's father" },
            { korean: "외할머니", roman: "oe-hal-meo-ni", english: "maternal grandmother", usage: "Mother's mother" },
            { korean: "삼촌", roman: "sam-chon", english: "uncle", usage: "Father's younger brother" },
            { korean: "이모", roman: "i-mo", english: "aunt", usage: "Mother's sister" },
            { korean: "사촌", roman: "sa-chon", english: "cousin", usage: "Same generation cousin" }
          ]
        },
        {
          title: "Marriage and In-Laws",
          content: "Terms for spouses and in-laws:",
          items: [
            { korean: "남편", roman: "nam-pyeon", english: "husband", usage: "When a woman refers to her husband" },
            { korean: "아내", roman: "a-nae", english: "wife", usage: "When a man refers to his wife" },
            { korean: "시아버지", roman: "si-a-beo-ji", english: "father-in-law", usage: "Husband's father" },
            { korean: "시어머니", roman: "si-eo-meo-ni", english: "mother-in-law", usage: "Husband's mother" },
            { korean: "장인", roman: "jang-in", english: "father-in-law", usage: "Wife's father" },
            { korean: "장모", roman: "jang-mo", english: "mother-in-law", usage: "Wife's mother" }
          ]
        }
      ],
      
      practice: {
        title: "Family Introduction Practice",
        instructions: "Practice introducing your family:",
        example: {
          korean: "안녕하세요. 제 가족을 소개해드릴게요. 저는 아버지, 어머니, 형이 있습니다. 저는 막내입니다.",
          roman: "An-nyeong-ha-se-yo. Je ga-jok-eul so-gae-hae-deu-ril-ge-yo. Jeo-neun a-beo-ji, eo-meo-ni, hyeong-i it-seum-ni-da. Jeo-neun mak-nae-im-ni-da.",
          english: "Hello. Let me introduce my family. I have a father, mother, and older brother. I am the youngest."
        }
      },
      
      tips: [
        "Korean family terms depend on the speaker's gender and age",
        "Always use formal language when talking about family to others",
        "Practice the different terms for older siblings based on your gender",
        "Remember that Korean culture places great importance on family hierarchy"
      ]
    }
  },
  
  advanced1: {
    title: "Honorifics and Politeness",
    difficulty: "Advanced",
    duration: "50 min",
    description: "Master Korean honorifics and politeness levels",
    icon: Award,
    content: {
      introduction: "Korean honorifics are crucial for showing respect and maintaining proper social relationships. Understanding these levels is essential for advanced Korean communication.",
      
      sections: [
        {
          title: "Speech Levels",
          content: "Different levels of formality in Korean:",
          items: [
            { korean: "-습니다/ㅂ니다", roman: "-seum-ni-da/-mnida", english: "formal polite", usage: "Business, presentations, formal situations" },
            { korean: "-어요/아요", roman: "-eo-yo/-a-yo", english: "polite", usage: "General polite speech, strangers, acquaintances" },
            { korean: "-어/아", roman: "-eo/-a", english: "casual", usage: "Close friends, family, younger people" },
            { korean: "-다", roman: "-da", english: "plain", usage: "Writing, thinking, very casual situations" }
          ]
        },
        {
          title: "Honorific Verbs",
          content: "Special honorific forms for showing respect:",
          items: [
            { korean: "드시다", roman: "deu-si-da", english: "to eat (honorific)", usage: "When someone older or respected eats" },
            { korean: "주무시다", roman: "ju-mu-si-da", english: "to sleep (honorific)", usage: "When someone older sleeps" },
            { korean: "계시다", roman: "gye-si-da", english: "to be (honorific)", usage: "When referring to someone older's existence" },
            { korean: "말씀하시다", roman: "mal-sseum-ha-si-da", english: "to speak (honorific)", usage: "When someone older speaks" }
          ]
        },
        {
          title: "Honorific Nouns",
          content: "Special honorific terms for people and things:",
          items: [
            { korean: "분", roman: "bun", english: "person (honorific)", usage: "Polite way to refer to a person" },
            { korean: "댁", roman: "daek", english: "house (honorific)", usage: "Polite way to refer to someone's house" },
            { korean: "성함", roman: "seong-ham", english: "name (honorific)", usage: "Polite way to ask for someone's name" },
            { korean: "나이", roman: "na-i", english: "age (honorific)", usage: "Polite way to ask about age" }
          ]
        }
      ],
      
      practice: {
        title: "Politeness Practice",
        instructions: "Choose the appropriate level of politeness for each situation:",
        scenarios: [
          { situation: "Talking to your professor", answer: "Use formal polite (-습니다/ㅂ니다) and honorifics" },
          { situation: "Talking to a stranger on the street", answer: "Use polite (-어요/아요)" },
          { situation: "Talking to your close friend", answer: "Use casual (-어/아)" },
          { situation: "Writing in a diary", answer: "Use plain (-다)" }
        ]
      },
      
      tips: [
        "When in doubt, use polite speech (-어요/아요)",
        "Always use honorifics when talking about or to elders",
        "Pay attention to the context and relationship",
        "Practice switching between different levels smoothly"
      ]
    }
  },
  
  beginner4: {
    title: "Numbers & Counting",
    difficulty: "Beginner",
    duration: "40 min",
    description: "Master Korean numbers and counting systems",
    icon: Target,
    content: {
      introduction: "Korean has two different number systems: Sino-Korean (based on Chinese) and Native Korean. Understanding when to use each system is crucial for proper Korean communication.",
      
      sections: [
        {
          title: "Sino-Korean Numbers (일, 이, 삼...)",
          content: "Used for dates, money, phone numbers, and mathematical operations:",
          items: [
            { korean: "일", roman: "il", english: "1", usage: "Dates, money, phone numbers" },
            { korean: "이", roman: "i", english: "2", usage: "Dates, money, phone numbers" },
            { korean: "삼", roman: "sam", english: "3", usage: "Dates, money, phone numbers" },
            { korean: "사", roman: "sa", english: "4", usage: "Dates, money, phone numbers" },
            { korean: "오", roman: "o", english: "5", usage: "Dates, money, phone numbers" },
            { korean: "육", roman: "yuk", english: "6", usage: "Dates, money, phone numbers" },
            { korean: "칠", roman: "chil", english: "7", usage: "Dates, money, phone numbers" },
            { korean: "팔", roman: "pal", english: "8", usage: "Dates, money, phone numbers" },
            { korean: "구", roman: "gu", english: "9", usage: "Dates, money, phone numbers" },
            { korean: "십", roman: "sip", english: "10", usage: "Dates, money, phone numbers" }
          ]
        },
        {
          title: "Native Korean Numbers (하나, 둘, 셋...)",
          content: "Used for counting objects, age, and general counting:",
          items: [
            { korean: "하나", roman: "ha-na", english: "1", usage: "Counting objects, age" },
            { korean: "둘", roman: "dul", english: "2", usage: "Counting objects, age" },
            { korean: "셋", roman: "set", english: "3", usage: "Counting objects, age" },
            { korean: "넷", roman: "net", english: "4", usage: "Counting objects, age" },
            { korean: "다섯", roman: "da-seot", english: "5", usage: "Counting objects, age" },
            { korean: "여섯", roman: "yeo-seot", english: "6", usage: "Counting objects, age" },
            { korean: "일곱", roman: "il-gop", english: "7", usage: "Counting objects, age" },
            { korean: "여덟", roman: "yeo-deol", english: "8", usage: "Counting objects, age" },
            { korean: "아홉", roman: "a-hop", english: "9", usage: "Counting objects, age" },
            { korean: "열", roman: "yeol", english: "10", usage: "Counting objects, age" }
          ]
        },
        {
          title: "Counting Objects",
          content: "Korean uses specific counters for different types of objects:",
          examples: [
            { korean: "사과 한 개", roman: "sa-gwa han gae", english: "one apple", usage: "개 = general counter for objects" },
            { korean: "책 두 권", roman: "chaek du gwon", english: "two books", usage: "권 = counter for books" },
            { korean: "사람 세 명", roman: "sa-ram se myeong", english: "three people", usage: "명 = counter for people" },
            { korean: "컵 네 개", roman: "keop ne gae", english: "four cups", usage: "개 = counter for cups" },
            { korean: "차 다섯 대", roman: "cha da-seot dae", english: "five cars", usage: "대 = counter for vehicles" }
          ]
        }
      ],
      
      practice: {
        title: "Number Practice",
        instructions: "Practice reading these numbers and situations:",
        scenarios: [
          { situation: "Your phone number is 010-1234-5678", answer: "공일공-일이삼사-오육칠팔" },
          { situation: "You are 25 years old", answer: "스물다섯 살" },
          { situation: "Today is March 15th", answer: "삼월 십오일" },
          { situation: "The price is 5,000 won", answer: "오천 원" }
        ]
      },
      
      tips: [
        "Use Sino-Korean for dates, money, phone numbers, and math",
        "Use Native Korean for counting objects and stating age",
        "Practice both systems until they become natural",
        "Remember that some numbers change form when used with counters"
      ]
    }
  },
  
  beginner5: {
    title: "Family & Relationships",
    difficulty: "Beginner",
    duration: "35 min",
    description: "Learn family terms and relationship vocabulary",
    icon: Users,
    content: {
      introduction: "Korean family relationships are complex and hierarchical. Understanding these terms is essential for Korean culture and communication.",
      
      sections: [
        {
          title: "Immediate Family",
          content: "Basic family members:",
          items: [
            { korean: "아버지", roman: "a-beo-ji", english: "father", usage: "Formal way to say father" },
            { korean: "어머니", roman: "eo-meo-ni", english: "mother", usage: "Formal way to say mother" },
            { korean: "아빠", roman: "a-ppa", english: "dad", usage: "Casual way to say father" },
            { korean: "엄마", roman: "eom-ma", english: "mom", usage: "Casual way to say mother" },
            { korean: "형", roman: "hyeong", english: "older brother (male speaking)", usage: "When a male talks about his older brother" },
            { korean: "누나", roman: "nu-na", english: "older sister (male speaking)", usage: "When a male talks about his older sister" },
            { korean: "오빠", roman: "o-ppa", english: "older brother (female speaking)", usage: "When a female talks about her older brother" },
            { korean: "언니", roman: "eon-ni", english: "older sister (female speaking)", usage: "When a female talks about her older sister" },
            { korean: "동생", roman: "dong-saeng", english: "younger sibling", usage: "General term for younger brother or sister" }
          ]
        },
        {
          title: "Extended Family",
          content: "Grandparents, aunts, uncles, and cousins:",
          items: [
            { korean: "할아버지", roman: "hal-a-beo-ji", english: "grandfather", usage: "Father's father" },
            { korean: "할머니", roman: "hal-meo-ni", english: "grandmother", usage: "Father's mother" },
            { korean: "외할아버지", roman: "oe-hal-a-beo-ji", english: "maternal grandfather", usage: "Mother's father" },
            { korean: "외할머니", roman: "oe-hal-meo-ni", english: "maternal grandmother", usage: "Mother's mother" },
            { korean: "삼촌", roman: "sam-chon", english: "uncle", usage: "Father's younger brother" },
            { korean: "이모", roman: "i-mo", english: "aunt", usage: "Mother's sister" },
            { korean: "사촌", roman: "sa-chon", english: "cousin", usage: "Same generation cousin" }
          ]
        },
        {
          title: "Marriage and In-Laws",
          content: "Terms for spouses and in-laws:",
          items: [
            { korean: "남편", roman: "nam-pyeon", english: "husband", usage: "When a woman refers to her husband" },
            { korean: "아내", roman: "a-nae", english: "wife", usage: "When a man refers to his wife" },
            { korean: "시아버지", roman: "si-a-beo-ji", english: "father-in-law", usage: "Husband's father" },
            { korean: "시어머니", roman: "si-eo-meo-ni", english: "mother-in-law", usage: "Husband's mother" },
            { korean: "장인", roman: "jang-in", english: "father-in-law", usage: "Wife's father" },
            { korean: "장모", roman: "jang-mo", english: "mother-in-law", usage: "Wife's mother" }
          ]
        }
      ],
      
      practice: {
        title: "Family Introduction Practice",
        instructions: "Practice introducing your family:",
        example: {
          korean: "안녕하세요. 제 가족을 소개해드릴게요. 저는 아버지, 어머니, 형이 있습니다. 저는 막내입니다.",
          roman: "An-nyeong-ha-se-yo. Je ga-jok-eul so-gae-hae-deu-ril-ge-yo. Jeo-neun a-beo-ji, eo-meo-ni, hyeong-i it-seum-ni-da. Jeo-neun mak-nae-im-ni-da.",
          english: "Hello. Let me introduce my family. I have a father, mother, and older brother. I am the youngest."
        }
      },
      
      tips: [
        "Korean family terms depend on the speaker's gender and age",
        "Always use formal language when talking about family to others",
        "Practice the different terms for older siblings based on your gender",
        "Remember that Korean culture places great importance on family hierarchy"
      ]
    }
  },
  
  beginner6: {
    title: "Time & Dates",
    difficulty: "Beginner",
    duration: "40 min",
    description: "Express time, dates, and schedules in Korean",
    icon: Clock,
    content: {
      introduction: "Learning to express time and dates is essential for daily Korean communication. This lesson covers time expressions, days of the week, months, and years.",
      
      sections: [
        {
          title: "Days of the Week",
          content: "Korean days of the week:",
          items: [
            { korean: "월요일", roman: "wol-yo-il", english: "Monday", usage: "First day of the week" },
            { korean: "화요일", roman: "hwa-yo-il", english: "Tuesday", usage: "Second day of the week" },
            { korean: "수요일", roman: "su-yo-il", english: "Wednesday", usage: "Third day of the week" },
            { korean: "목요일", roman: "mok-yo-il", english: "Thursday", usage: "Fourth day of the week" },
            { korean: "금요일", roman: "geum-yo-il", english: "Friday", usage: "Fifth day of the week" },
            { korean: "토요일", roman: "to-yo-il", english: "Saturday", usage: "Sixth day of the week" },
            { korean: "일요일", roman: "il-yo-il", english: "Sunday", usage: "Seventh day of the week" }
          ]
        },
        {
          title: "Months",
          content: "Korean months:",
          items: [
            { korean: "일월", roman: "il-wol", english: "January", usage: "First month" },
            { korean: "이월", roman: "i-wol", english: "February", usage: "Second month" },
            { korean: "삼월", roman: "sam-wol", english: "March", usage: "Third month" },
            { korean: "사월", roman: "sa-wol", english: "April", usage: "Fourth month" },
            { korean: "오월", roman: "o-wol", english: "May", usage: "Fifth month" },
            { korean: "육월", roman: "yuk-wol", english: "June", usage: "Sixth month" },
            { korean: "칠월", roman: "chil-wol", english: "July", usage: "Seventh month" },
            { korean: "팔월", roman: "pal-wol", english: "August", usage: "Eighth month" },
            { korean: "구월", roman: "gu-wol", english: "September", usage: "Ninth month" },
            { korean: "십월", roman: "sip-wol", english: "October", usage: "Tenth month" },
            { korean: "십일월", roman: "sip-il-wol", english: "November", usage: "Eleventh month" },
            { korean: "십이월", roman: "sip-i-wol", english: "December", usage: "Twelfth month" }
          ]
        },
        {
          title: "Time Expressions",
          content: "Common time-related phrases:",
          items: [
            { korean: "지금", roman: "ji-geum", english: "now", usage: "Current time" },
            { korean: "오늘", roman: "o-neul", english: "today", usage: "This day" },
            { korean: "어제", roman: "eo-je", english: "yesterday", usage: "Previous day" },
            { korean: "내일", roman: "nae-il", english: "tomorrow", usage: "Next day" },
            { korean: "아침", roman: "a-chim", english: "morning", usage: "Early part of day" },
            { korean: "점심", roman: "jeom-sim", english: "lunch/noon", usage: "Midday meal" },
            { korean: "저녁", roman: "jeo-nyeok", english: "evening", usage: "Late part of day" },
            { korean: "밤", roman: "bam", english: "night", usage: "Dark hours" }
          ]
        }
      ],
      
      practice: {
        title: "Time Practice",
        instructions: "Practice these time expressions:",
        scenarios: [
          { situation: "What day is today?", answer: "오늘은 무슨 요일입니까?" },
          { situation: "What time is it now?", answer: "지금 몇 시입니까?" },
          { situation: "When is your birthday?", answer: "생일이 언제입니까?" },
          { situation: "See you tomorrow", answer: "내일 봐요" }
        ]
      },
      
      tips: [
        "Korean uses Sino-Korean numbers for dates and times",
        "Practice saying dates in the format: year년 month월 day일",
        "Learn the days of the week as they're used frequently",
        "Remember that Korean culture has different concepts of time"
      ]
    }
  },
  
  beginner7: {
    title: "Food & Dining",
    difficulty: "Beginner",
    duration: "45 min",
    description: "Order food and discuss meals in Korean",
    icon: BookOpen,
    content: {
      introduction: "Food is central to Korean culture. Learning food vocabulary and dining expressions will help you navigate Korean restaurants and social situations.",
      
      sections: [
        {
          title: "Basic Food Items",
          content: "Common Korean foods:",
          items: [
            { korean: "밥", roman: "bap", english: "rice/meal", usage: "Staple food" },
            { korean: "김치", roman: "gim-chi", english: "kimchi", usage: "Fermented vegetables" },
            { korean: "국", roman: "guk", english: "soup", usage: "Korean soup" },
            { korean: "고기", roman: "go-gi", english: "meat", usage: "General term for meat" },
            { korean: "생선", roman: "saeng-seon", english: "fish", usage: "Seafood" },
            { korean: "야채", roman: "ya-chae", english: "vegetables", usage: "Plant foods" },
            { korean: "과일", roman: "gwa-il", english: "fruit", usage: "Sweet plant foods" },
            { korean: "물", roman: "mul", english: "water", usage: "Basic drink" }
          ]
        },
        {
          title: "Dining Expressions",
          content: "Common phrases when eating:",
          items: [
            { korean: "맛있어요", roman: "mat-it-seo-yo", english: "It's delicious", usage: "Complimenting food" },
            { korean: "배고파요", roman: "bae-go-pa-yo", english: "I'm hungry", usage: "Expressing hunger" },
            { korean: "배불러요", roman: "bae-bul-leo-yo", english: "I'm full", usage: "After eating enough" },
            { korean: "잘 먹겠습니다", roman: "jal meok-get-seum-ni-da", english: "I will eat well", usage: "Before starting to eat" },
            { korean: "잘 먹었습니다", roman: "jal meo-geot-seum-ni-da", english: "I ate well", usage: "After finishing eating" },
            { korean: "추가해 주세요", roman: "chu-ga-hae ju-se-yo", english: "Please add more", usage: "Asking for more food" }
          ]
        },
        {
          title: "Restaurant Phrases",
          content: "Ordering food at restaurants:",
          items: [
            { korean: "메뉴 주세요", roman: "me-nyu ju-se-yo", english: "Please give me the menu", usage: "Asking for menu" },
            { korean: "이것 주세요", roman: "i-geot ju-se-yo", english: "Please give me this", usage: "Pointing to order" },
            { korean: "계산해 주세요", roman: "gye-san-hae ju-se-yo", english: "Please calculate the bill", usage: "Asking for check" },
            { korean: "포장해 주세요", roman: "po-jang-hae ju-se-yo", english: "Please wrap it up", usage: "Takeout request" },
            { korean: "여기 앉아도 돼요?", roman: "yeo-gi an-ja-do dwae-yo?", english: "Can I sit here?", usage: "Asking for seating" }
          ]
        }
      ],
      
      practice: {
        title: "Dining Practice",
        instructions: "Practice these restaurant scenarios:",
        scenarios: [
          { situation: "Ordering kimchi jjigae", answer: "김치찌개 주세요" },
          { situation: "Asking if food is spicy", answer: "맵나요?" },
          { situation: "Complimenting the food", answer: "정말 맛있어요!" },
          { situation: "Asking for the bill", answer: "계산해 주세요" }
        ]
      },
      
      tips: [
        "Korean meals are often shared, so learn communal dining etiquette",
        "Practice saying '잘 먹겠습니다' before eating",
        "Learn to use chopsticks properly",
        "Korean food can be spicy, so learn to ask about spice levels"
      ]
    }
  },
  
  beginner8: {
    title: "Shopping & Money",
    difficulty: "Beginner",
    duration: "35 min",
    description: "Navigate shopping and handle transactions",
    icon: Target,
    content: {
      introduction: "Shopping vocabulary is essential for daily life in Korea. This lesson covers money, shopping phrases, and common items you'll need to buy.",
      
      sections: [
        {
          title: "Money and Currency",
          content: "Korean money terms:",
          items: [
            { korean: "원", roman: "won", english: "won (Korean currency)", usage: "Korean money unit" },
            { korean: "돈", roman: "don", english: "money", usage: "General term for money" },
            { korean: "가격", roman: "ga-gyeok", english: "price", usage: "Cost of item" },
            { korean: "할인", roman: "hal-in", english: "discount", usage: "Price reduction" },
            { korean: "현금", roman: "hyeon-geum", english: "cash", usage: "Physical money" },
            { korean: "카드", roman: "ka-deu", english: "card", usage: "Credit/debit card" },
            { korean: "영수증", roman: "yeong-su-jeung", english: "receipt", usage: "Payment proof" }
          ]
        },
        {
          title: "Shopping Phrases",
          content: "Common shopping expressions:",
          items: [
            { korean: "얼마예요?", roman: "eol-ma-ye-yo?", english: "How much is it?", usage: "Asking for price" },
            { korean: "이거 주세요", roman: "i-geo ju-se-yo", english: "Please give me this", usage: "Making purchase" },
            { korean: "다른 색깔 있어요?", roman: "da-reun saek-kkal it-seo-yo?", english: "Do you have other colors?", usage: "Asking for variety" },
            { korean: "사이즈 있어요?", roman: "sa-i-jeu it-seo-yo?", english: "Do you have sizes?", usage: "Asking about sizes" },
            { korean: "더 큰 거 있어요?", roman: "deo keun geo it-seo-yo?", english: "Do you have a bigger one?", usage: "Asking for larger size" },
            { korean: "더 작은 거 있어요?", roman: "deo ja-geun geo it-seo-yo?", english: "Do you have a smaller one?", usage: "Asking for smaller size" }
          ]
        },
        {
          title: "Colors and Sizes",
          content: "Common colors and size terms:",
          items: [
            { korean: "빨간색", roman: "ppal-gan-saek", english: "red", usage: "Color description" },
            { korean: "파란색", roman: "pa-ran-saek", english: "blue", usage: "Color description" },
            { korean: "노란색", roman: "no-ran-saek", english: "yellow", usage: "Color description" },
            { korean: "검은색", roman: "geom-eun-saek", english: "black", usage: "Color description" },
            { korean: "흰색", roman: "hin-saek", english: "white", usage: "Color description" },
            { korean: "크다", roman: "keu-da", english: "big/large", usage: "Size description" },
            { korean: "작다", roman: "jak-da", english: "small", usage: "Size description" },
            { korean: "길다", roman: "gil-da", english: "long", usage: "Length description" },
            { korean: "짧다", roman: "jjal-da", english: "short", usage: "Length description" }
          ]
        }
      ],
      
      practice: {
        title: "Shopping Practice",
        instructions: "Practice these shopping scenarios:",
        scenarios: [
          { situation: "Asking for price of a shirt", answer: "이 셔츠 얼마예요?" },
          { situation: "Asking for different color", answer: "다른 색깔 있어요?" },
          { situation: "Asking for smaller size", answer: "더 작은 사이즈 있어요?" },
          { situation: "Paying with card", answer: "카드로 계산할게요" }
        ]
      },
      
      tips: [
        "Korean prices are often written in thousands (천) or ten thousands (만)",
        "Practice numbers well as they're essential for shopping",
        "Learn to bargain politely if shopping at markets",
        "Always ask for a receipt (영수증) when making purchases"
      ]
    }
  },
  
  beginner9: {
    title: "Time & Dates",
    difficulty: "Beginner",
    duration: "35 min",
    description: "Tell time and discuss dates in Korean",
    icon: Target,
    content: {
      introduction: "Understanding time and dates is essential for daily communication in Korean. This lesson covers telling time, discussing dates, and time-related expressions.",
      
      sections: [
        {
          title: "Telling Time",
          content: "How to tell time in Korean:",
          items: [
            { korean: "지금", roman: "ji-geum", english: "now", usage: "Current time" },
            { korean: "몇 시", roman: "myeot si", english: "what time", usage: "Asking for time" },
            { korean: "시", roman: "si", english: "hour", usage: "Time unit" },
            { korean: "분", roman: "bun", english: "minute", usage: "Time unit" },
            { korean: "오전", roman: "o-jeon", english: "AM", usage: "Morning time" },
            { korean: "오후", roman: "o-hu", english: "PM", usage: "Afternoon/evening time" }
          ]
        },
        {
          title: "Days of the Week",
          content: "Korean days of the week:",
          items: [
            { korean: "월요일", roman: "wol-yo-il", english: "Monday", usage: "First day of work week" },
            { korean: "화요일", roman: "hwa-yo-il", english: "Tuesday", usage: "Second day" },
            { korean: "수요일", roman: "su-yo-il", english: "Wednesday", usage: "Middle of week" },
            { korean: "목요일", roman: "mok-yo-il", english: "Thursday", usage: "Fourth day" },
            { korean: "금요일", roman: "geum-yo-il", english: "Friday", usage: "Last work day" },
            { korean: "토요일", roman: "to-yo-il", english: "Saturday", usage: "Weekend" },
            { korean: "일요일", roman: "il-yo-il", english: "Sunday", usage: "Weekend" }
          ]
        },
        {
          title: "Months and Dates",
          content: "Months and date expressions:",
          items: [
            { korean: "월", roman: "wol", english: "month", usage: "Month indicator" },
            { korean: "일", roman: "il", english: "day", usage: "Day indicator" },
            { korean: "년", roman: "nyeon", english: "year", usage: "Year indicator" },
            { korean: "오늘", roman: "o-neul", english: "today", usage: "This day" },
            { korean: "어제", roman: "eo-je", english: "yesterday", usage: "Previous day" },
            { korean: "내일", roman: "nae-il", english: "tomorrow", usage: "Next day" }
          ]
        }
      ],
      
      practice: {
        title: "Time Practice",
        instructions: "Practice these time expressions:",
        scenarios: [
          { situation: "What time is it now?", answer: "지금 몇 시예요?" },
          { situation: "What day is today?", answer: "오늘은 무슨 요일이에요?" },
          { situation: "When is your birthday?", answer: "생일이 언제예요?" },
          { situation: "See you tomorrow", answer: "내일 봐요" }
        ]
      },
      
      tips: [
        "Korean uses Sino-Korean numbers for dates and times",
        "Practice saying dates in the format: year년 month월 day일",
        "Learn the days of the week as they're used frequently",
        "Remember that Korean culture has different concepts of time"
      ]
    }
  },
  
  beginner10: {
    title: "Colors & Descriptions",
    difficulty: "Beginner",
    duration: "35 min",
    description: "Learn colors and descriptive vocabulary",
    icon: Target,
    content: {
      introduction: "Colors and descriptive words are essential for expressing yourself in Korean. This lesson covers basic colors, common adjectives, and how to describe objects and people.",
      
      sections: [
        {
          title: "Basic Colors",
          content: "Essential color vocabulary:",
          items: [
            { korean: "빨간색", roman: "ppal-gan-saek", english: "red", usage: "Most common red" },
            { korean: "파란색", roman: "pa-ran-saek", english: "blue", usage: "Most common blue" },
            { korean: "노란색", roman: "no-ran-saek", english: "yellow", usage: "Most common yellow" },
            { korean: "초록색", roman: "cho-rok-saek", english: "green", usage: "Most common green" },
            { korean: "보라색", roman: "bo-ra-saek", english: "purple", usage: "Most common purple" },
            { korean: "검은색", roman: "geom-eun-saek", english: "black", usage: "Most common black" },
            { korean: "흰색", roman: "hin-saek", english: "white", usage: "Most common white" },
            { korean: "회색", roman: "hoe-saek", english: "gray", usage: "Most common gray" }
          ]
        },
        {
          title: "Common Adjectives",
          content: "Frequently used descriptive words:",
          items: [
            { korean: "크다", roman: "keu-da", english: "big/large", usage: "Describing size" },
            { korean: "작다", roman: "jak-da", english: "small", usage: "Describing size" },
            { korean: "길다", roman: "gil-da", english: "long", usage: "Describing length" },
            { korean: "짧다", roman: "jjal-da", english: "short", usage: "Describing length" },
            { korean: "높다", roman: "nop-da", english: "high/tall", usage: "Describing height" },
            { korean: "낮다", roman: "nat-da", english: "low/short", usage: "Describing height" },
            { korean: "좋다", roman: "jot-da", english: "good", usage: "Positive description" },
            { korean: "나쁘다", roman: "na-ppeu-da", english: "bad", usage: "Negative description" }
          ]
        }
      ],
      
      practice: {
        title: "Description Practice",
        instructions: "Practice describing these objects:",
        scenarios: [
          { situation: "Describe a red apple", answer: "빨간 사과" },
          { situation: "Describe a big house", answer: "큰 집" },
          { situation: "Describe a long road", answer: "긴 길" },
          { situation: "Describe a good movie", answer: "좋은 영화" }
        ]
      },
      
      tips: [
        "Colors can be used as nouns or adjectives in Korean",
        "Adjectives come before the noun they describe",
        "Practice combining colors with other descriptive words",
        "Learn to describe both objects and people"
      ]
    }
  },
  
  beginner11: {
    title: "Transportation",
    difficulty: "Beginner",
    duration: "40 min",
    description: "Navigate Korean transportation systems",
    icon: Target,
    content: {
      introduction: "Getting around Korea requires knowledge of transportation vocabulary. This lesson covers public transportation, directions, and travel-related phrases.",
      
      sections: [
        {
          title: "Public Transportation",
          content: "Common transportation methods:",
          items: [
            { korean: "지하철", roman: "ji-ha-cheol", english: "subway", usage: "Most common in Seoul" },
            { korean: "버스", roman: "beo-seu", english: "bus", usage: "City and intercity buses" },
            { korean: "택시", roman: "taek-si", english: "taxi", usage: "Private transportation" },
            { korean: "기차", roman: "gi-cha", english: "train", usage: "Long distance travel" },
            { korean: "비행기", roman: "bi-haeng-gi", english: "airplane", usage: "International travel" },
            { korean: "자동차", roman: "ja-dong-cha", english: "car", usage: "Private vehicle" }
          ]
        },
        {
          title: "Directions & Locations",
          content: "Essential direction vocabulary:",
          items: [
            { korean: "앞", roman: "ap", english: "front", usage: "Direction indicator" },
            { korean: "뒤", roman: "dwi", english: "back", usage: "Direction indicator" },
            { korean: "왼쪽", roman: "oen-jjok", english: "left", usage: "Direction indicator" },
            { korean: "오른쪽", roman: "o-reun-jjok", english: "right", usage: "Direction indicator" },
            { korean: "위", roman: "wi", english: "up/above", usage: "Position indicator" },
            { korean: "아래", roman: "a-rae", english: "down/below", usage: "Position indicator" },
            { korean: "가까이", roman: "ga-kka-i", english: "near", usage: "Distance indicator" },
            { korean: "멀리", roman: "meol-li", english: "far", usage: "Distance indicator" }
          ]
        }
      ],
      
      practice: {
        title: "Transportation Practice",
        instructions: "Practice these travel scenarios:",
        scenarios: [
          { situation: "Ask where the subway station is", answer: "지하철역이 어디에 있어요?" },
          { situation: "Ask for directions to the bus stop", answer: "버스 정류장으로 가는 길을 알려주세요" },
          { situation: "Ask how to get to the airport", answer: "공항에 어떻게 가나요?" },
          { situation: "Ask if this bus goes to downtown", answer: "이 버스는 시내로 가나요?" }
        ]
      },
      
      tips: [
        "Korean public transportation is very efficient and affordable",
        "Learn to read Korean station names and bus numbers",
        "Practice asking for directions politely",
        "Familiarize yourself with Korean transportation apps"
      ]
    }
  },
  
  beginner12: {
    title: "Weather & Seasons",
    difficulty: "Beginner",
    duration: "30 min",
    description: "Discuss weather and seasonal activities",
    icon: Target,
    content: {
      introduction: "Weather is a common topic of conversation in Korea. Learning weather vocabulary and seasonal expressions will help you engage in everyday conversations.",
      
      sections: [
        {
          title: "Weather Conditions",
          content: "Common weather vocabulary:",
          items: [
            { korean: "날씨", roman: "nal-ssi", english: "weather", usage: "General weather" },
            { korean: "맑다", roman: "mak-da", english: "clear", usage: "Clear sky" },
            { korean: "흐리다", roman: "heu-ri-da", english: "cloudy", usage: "Cloudy sky" },
            { korean: "비", roman: "bi", english: "rain", usage: "Rainy weather" },
            { korean: "눈", roman: "nun", english: "snow", usage: "Snowy weather" },
            { korean: "바람", roman: "ba-ram", english: "wind", usage: "Windy weather" },
            { korean: "따뜻하다", roman: "tta-tteut-ha-da", english: "warm", usage: "Warm weather" },
            { korean: "춥다", roman: "chup-da", english: "cold", usage: "Cold weather" }
          ]
        },
        {
          title: "Seasons",
          content: "Four seasons in Korean:",
          items: [
            { korean: "봄", roman: "bom", english: "spring", usage: "Spring season" },
            { korean: "여름", roman: "yeo-reum", english: "summer", usage: "Summer season" },
            { korean: "가을", roman: "ga-eul", english: "autumn", usage: "Fall season" },
            { korean: "겨울", roman: "gyeo-ul", english: "winter", usage: "Winter season" },
            { korean: "계절", roman: "gye-jeol", english: "season", usage: "General season" },
            { korean: "기온", roman: "gi-on", english: "temperature", usage: "Weather temperature" },
            { korean: "습도", roman: "seup-do", english: "humidity", usage: "Moisture level" },
            { korean: "예보", roman: "ye-bo", english: "forecast", usage: "Weather prediction" }
          ]
        }
      ],
      
      practice: {
        title: "Weather Practice",
        instructions: "Practice these weather scenarios:",
        scenarios: [
          { situation: "Ask about today's weather", answer: "오늘 날씨가 어때요?" },
          { situation: "Describe rainy weather", answer: "비가 와요" },
          { situation: "Talk about favorite season", answer: "좋아하는 계절이 뭐예요?" },
          { situation: "Ask about temperature", answer: "기온이 몇 도예요?" }
        ]
      },
      
      tips: [
        "Weather is a safe topic for small talk in Korea",
        "Learn seasonal activities and foods",
        "Practice describing weather conditions",
        "Understand Korean seasonal customs and traditions"
      ]
    }
  },
  
  intermediate1: {
    title: "Honorifics & Formality",
    difficulty: "Intermediate",
    duration: "45 min",
    description: "Understand Korean politeness levels and honorifics",
    icon: Award,
    content: {
      introduction: "Korean honorifics are crucial for showing respect and maintaining proper social relationships. Understanding these levels is essential for advanced Korean communication.",
      
      sections: [
        {
          title: "Speech Levels",
          content: "Different levels of formality in Korean:",
          items: [
            { korean: "-습니다/ㅂ니다", roman: "-seum-ni-da/-mnida", english: "formal polite", usage: "Business, presentations, formal situations" },
            { korean: "-어요/아요", roman: "-eo-yo/-a-yo", english: "polite", usage: "General polite speech, strangers, acquaintances" },
            { korean: "-어/아", roman: "-eo/-a", english: "casual", usage: "Close friends, family, younger people" },
            { korean: "-다", roman: "-da", english: "plain", usage: "Writing, thinking, very casual situations" }
          ]
        },
        {
          title: "Honorific Verbs",
          content: "Special honorific forms for showing respect:",
          items: [
            { korean: "드시다", roman: "deu-si-da", english: "to eat (honorific)", usage: "When someone older or respected eats" },
            { korean: "주무시다", roman: "ju-mu-si-da", english: "to sleep (honorific)", usage: "When someone older sleeps" },
            { korean: "계시다", roman: "gye-si-da", english: "to be (honorific)", usage: "When referring to someone older's existence" },
            { korean: "말씀하시다", roman: "mal-sseum-ha-si-da", english: "to speak (honorific)", usage: "When someone older speaks" }
          ]
        },
        {
          title: "Honorific Nouns",
          content: "Special honorific terms for people and things:",
          items: [
            { korean: "분", roman: "bun", english: "person (honorific)", usage: "Polite way to refer to a person" },
            { korean: "댁", roman: "daek", english: "house (honorific)", usage: "Polite way to refer to someone's house" },
            { korean: "성함", roman: "seong-ham", english: "name (honorific)", usage: "Polite way to ask for someone's name" },
            { korean: "나이", roman: "na-i", english: "age (honorific)", usage: "Polite way to ask about age" }
          ]
        }
      ],
      
      practice: {
        title: "Politeness Practice",
        instructions: "Choose the appropriate level of politeness for each situation:",
        scenarios: [
          { situation: "Talking to your professor", answer: "Use formal polite (-습니다/ㅂ니다) and honorifics" },
          { situation: "Talking to a stranger on the street", answer: "Use polite (-어요/아요)" },
          { situation: "Talking to your close friend", answer: "Use casual (-어/아)" },
          { situation: "Writing in a diary", answer: "Use plain (-다)" }
        ]
      },
      
      tips: [
        "When in doubt, use polite speech (-어요/아요)",
        "Always use honorifics when talking about or to elders",
        "Pay attention to the context and relationship",
        "Practice switching between different levels smoothly"
      ]
    }
  },
  
  intermediate4: {
    title: "Directions & Location",
    difficulty: "Intermediate",
    duration: "40 min",
    description: "Ask for and give directions in Korean",
    icon: Target,
    content: {
      introduction: "Learning to ask for and give directions is essential for navigating Korean cities. This lesson covers location words, direction verbs, and common phrases for getting around.",
      
      sections: [
        {
          title: "Location Words",
          content: "Basic location vocabulary:",
          items: [
            { korean: "여기", roman: "yeo-gi", english: "here", usage: "Current location" },
            { korean: "거기", roman: "geo-gi", english: "there", usage: "That location" },
            { korean: "저기", roman: "jeo-gi", english: "over there", usage: "Distant location" },
            { korean: "앞", roman: "ap", english: "front", usage: "In front of" },
            { korean: "뒤", roman: "dwi", english: "back", usage: "Behind" },
            { korean: "왼쪽", roman: "oen-jjok", english: "left", usage: "Left side" },
            { korean: "오른쪽", roman: "o-reun-jjok", english: "right", usage: "Right side" },
            { korean: "위", roman: "wi", english: "up/above", usage: "Above something" },
            { korean: "아래", roman: "a-rae", english: "down/below", usage: "Below something" }
          ]
        },
        {
          title: "Direction Verbs",
          content: "Verbs for movement and direction:",
          items: [
            { korean: "가다", roman: "ga-da", english: "to go", usage: "General movement" },
            { korean: "오다", roman: "o-da", english: "to come", usage: "Movement toward speaker" },
            { korean: "돌다", roman: "dol-da", english: "to turn", usage: "Change direction" },
            { korean: "건너가다", roman: "geon-neo-ga-da", english: "to cross", usage: "Go across" },
            { korean: "올라가다", roman: "ol-la-ga-da", english: "to go up", usage: "Upward movement" },
            { korean: "내려가다", roman: "nae-ryeo-ga-da", english: "to go down", usage: "Downward movement" },
            { korean: "지나가다", roman: "ji-na-ga-da", english: "to pass by", usage: "Go past something" }
          ]
        },
        {
          title: "Asking for Directions",
          content: "Common phrases for asking directions:",
          items: [
            { korean: "어디예요?", roman: "eo-di-ye-yo?", english: "Where is it?", usage: "Asking for location" },
            { korean: "어떻게 가요?", roman: "eo-tteo-ke ga-yo?", english: "How do I get there?", usage: "Asking for directions" },
            { korean: "가까워요?", roman: "ga-kka-wo-yo?", english: "Is it close?", usage: "Asking about distance" },
            { korean: "얼마나 걸려요?", roman: "eol-ma-na geol-lyeo-yo?", english: "How long does it take?", usage: "Asking about time" },
            { korean: "몇 분 걸려요?", roman: "myeot bun geol-lyeo-yo?", english: "How many minutes?", usage: "Asking about duration" },
            { korean: "버스로 갈 수 있어요?", roman: "beo-seu-ro gal su it-seo-yo?", english: "Can I go by bus?", usage: "Asking about transportation" }
          ]
        }
      ],
      
      practice: {
        title: "Direction Practice",
        instructions: "Practice these direction scenarios:",
        scenarios: [
          { situation: "Asking where the station is", answer: "역이 어디예요?" },
          { situation: "Asking how to get to the hospital", answer: "병원에 어떻게 가요?" },
          { situation: "Asking if it's far", answer: "멀어요?" },
          { situation: "Asking how long it takes", answer: "얼마나 걸려요?" }
        ]
      },
      
      tips: [
        "Korean directions are often given using landmarks",
        "Learn common landmarks like 역 (station), 병원 (hospital), 은행 (bank)",
        "Practice with a map to visualize directions",
        "Don't hesitate to ask for clarification if directions are unclear"
      ]
    }
  },
  
  intermediate5: {
    title: "Emotions & Feelings",
    difficulty: "Intermediate",
    duration: "35 min",
    description: "Express emotions and describe feelings",
    icon: Users,
    content: {
      introduction: "Being able to express emotions and feelings is important for building relationships and communicating effectively in Korean.",
      
      sections: [
        {
          title: "Basic Emotions",
          content: "Common emotional expressions:",
          items: [
            { korean: "기뻐요", roman: "gi-ppeo-yo", english: "I'm happy", usage: "Expressing joy" },
            { korean: "슬퍼요", roman: "seul-peo-yo", english: "I'm sad", usage: "Expressing sadness" },
            { korean: "화나요", roman: "hwa-na-yo", english: "I'm angry", usage: "Expressing anger" },
            { korean: "무서워요", roman: "mu-seo-wo-yo", english: "I'm scared", usage: "Expressing fear" },
            { korean: "놀라요", roman: "nol-la-yo", english: "I'm surprised", usage: "Expressing surprise" },
            { korean: "걱정돼요", roman: "geok-jeong-dwae-yo", english: "I'm worried", usage: "Expressing concern" },
            { korean: "피곤해요", roman: "pi-gon-hae-yo", english: "I'm tired", usage: "Expressing fatigue" },
            { korean: "스트레스받아요", roman: "seu-teu-re-seu-ba-da-yo", english: "I'm stressed", usage: "Expressing stress" }
          ]
        },
        {
          title: "Physical Feelings",
          content: "Describing physical sensations:",
          items: [
            { korean: "아파요", roman: "a-pa-yo", english: "It hurts", usage: "Expressing pain" },
            { korean: "아프다", roman: "a-peu-da", english: "painful", usage: "Describing pain" },
            { korean: "따뜻해요", roman: "tta-tteu-tae-yo", english: "It's warm", usage: "Describing temperature" },
            { korean: "차가워요", roman: "cha-ga-wo-yo", english: "It's cold", usage: "Describing temperature" },
            { korean: "배고파요", roman: "bae-go-pa-yo", english: "I'm hungry", usage: "Expressing hunger" },
            { korean: "목말라요", roman: "mok-mal-la-yo", english: "I'm thirsty", usage: "Expressing thirst" },
            { korean: "졸려요", roman: "jol-lyeo-yo", english: "I'm sleepy", usage: "Expressing sleepiness" }
          ]
        },
        {
          title: "Emotional Intensity",
          content: "Expressing degree of emotion:",
          items: [
            { korean: "정말", roman: "jeong-mal", english: "really", usage: "Intensifier" },
            { korean: "너무", roman: "neo-mu", english: "too/very", usage: "Intensifier" },
            { korean: "조금", roman: "jo-geum", english: "a little", usage: "Mild degree" },
            { korean: "많이", roman: "ma-ni", english: "a lot", usage: "High degree" },
            { korean: "전혀", roman: "jeon-hyeo", english: "not at all", usage: "Negation" },
            { korean: "조금도", roman: "jo-geum-do", english: "not even a little", usage: "Strong negation" }
          ]
        }
      ],
      
      practice: {
        title: "Emotion Practice",
        instructions: "Practice expressing these emotions:",
        scenarios: [
          { situation: "You're very happy about your test results", answer: "시험 결과가 정말 기뻐요!" },
          { situation: "You're a little worried about tomorrow", answer: "내일이 조금 걱정돼요" },
          { situation: "You're not hungry at all", answer: "전혀 배고프지 않아요" },
          { situation: "You're really tired from work", answer: "일 때문에 정말 피곤해요" }
        ]
      },
      
      tips: [
        "Korean culture values emotional restraint in public",
        "Use appropriate intensity words to match the situation",
        "Practice facial expressions that match the emotions",
        "Learn to ask about others' feelings to show care"
      ]
    }
  },
  
  intermediate6: {
    title: "Weather & Seasons",
    difficulty: "Intermediate",
    duration: "40 min",
    description: "Discuss weather and seasonal activities",
    icon: Clock,
    content: {
      introduction: "Weather is a common topic of conversation in Korea. Learning weather vocabulary and seasonal expressions will help you engage in everyday conversations.",
      
      sections: [
        {
          title: "Weather Conditions",
          content: "Basic weather vocabulary:",
          items: [
            { korean: "날씨", roman: "nal-ssi", english: "weather", usage: "General weather term" },
            { korean: "맑아요", roman: "mal-ga-yo", english: "It's clear", usage: "Sunny weather" },
            { korean: "흐려요", roman: "heu-ryeo-yo", english: "It's cloudy", usage: "Cloudy weather" },
            { korean: "비 와요", roman: "bi wa-yo", english: "It's raining", usage: "Rainy weather" },
            { korean: "눈 와요", roman: "nun wa-yo", english: "It's snowing", usage: "Snowy weather" },
            { korean: "바람 불어요", roman: "ba-ram bu-leo-yo", english: "It's windy", usage: "Windy weather" },
            { korean: "더워요", roman: "deo-wo-yo", english: "It's hot", usage: "Hot weather" },
            { korean: "추워요", roman: "chu-wo-yo", english: "It's cold", usage: "Cold weather" }
          ]
        },
        {
          title: "Seasons",
          content: "Korean seasons:",
          items: [
            { korean: "봄", roman: "bom", english: "spring", usage: "March-May" },
            { korean: "여름", roman: "yeo-reum", english: "summer", usage: "June-August" },
            { korean: "가을", roman: "ga-eul", english: "autumn", usage: "September-November" },
            { korean: "겨울", roman: "gyeo-ul", english: "winter", usage: "December-February" },
            { korean: "계절", roman: "gye-jeol", english: "season", usage: "General term for seasons" }
          ]
        },
        {
          title: "Seasonal Activities",
          content: "Activities for each season:",
          items: [
            { korean: "꽃구경", roman: "kkot-gu-gyeong", english: "flower viewing", usage: "Spring activity" },
            { korean: "수영", roman: "su-yeong", english: "swimming", usage: "Summer activity" },
            { korean: "단풍구경", roman: "dan-pung-gu-gyeong", english: "autumn leaves viewing", usage: "Autumn activity" },
            { korean: "스키", roman: "seu-ki", english: "skiing", usage: "Winter activity" },
            { korean: "피크닉", roman: "pi-keu-nik", english: "picnic", usage: "Outdoor activity" },
            { korean: "등산", roman: "deung-san", english: "hiking", usage: "Mountain activity" }
          ]
        }
      ],
      
      practice: {
        title: "Weather Practice",
        instructions: "Practice these weather conversations:",
        scenarios: [
          { situation: "Asking about today's weather", answer: "오늘 날씨가 어때요?" },
          { situation: "Saying it's hot today", answer: "오늘 정말 더워요" },
          { situation: "Asking about favorite season", answer: "좋아하는 계절이 뭐예요?" },
          { situation: "Saying you like spring", answer: "봄을 좋아해요" }
        ]
      },
      
      tips: [
        "Korean weather can be extreme, so learn temperature expressions",
        "Seasons are important in Korean culture and cuisine",
        "Practice describing weather changes throughout the day",
        "Learn seasonal foods and activities for cultural context"
      ]
    }
  },
  
  intermediate10: {
    title: "Hobbies & Interests",
    difficulty: "Intermediate",
    duration: "35 min",
    description: "Talk about hobbies and personal interests",
    icon: Target,
    content: {
      introduction: "Sharing hobbies and interests is a great way to connect with Korean speakers. This lesson covers common hobbies and how to discuss personal interests.",
      
      sections: [
        {
          title: "Common Hobbies",
          content: "Popular hobby vocabulary:",
          items: [
            { korean: "취미", roman: "chwi-mi", english: "hobby", usage: "General term for hobby" },
            { korean: "독서", roman: "dok-seo", english: "reading", usage: "Reading books" },
            { korean: "영화보기", roman: "yeong-hwa-bo-gi", english: "watching movies", usage: "Movie watching" },
            { korean: "음악듣기", roman: "eum-ak-deut-gi", english: "listening to music", usage: "Music listening" },
            { korean: "운동", roman: "un-dong", english: "exercise", usage: "Physical activity" },
            { korean: "요리", roman: "yo-ri", english: "cooking", usage: "Food preparation" },
            { korean: "여행", roman: "yeo-haeng", english: "travel", usage: "Going to different places" },
            { korean: "사진찍기", roman: "sa-jin-jjik-gi", english: "photography", usage: "Taking pictures" }
          ]
        },
        {
          title: "Sports and Activities",
          content: "Sports and physical activities:",
          items: [
            { korean: "축구", roman: "chuk-gu", english: "soccer", usage: "Football" },
            { korean: "농구", roman: "nong-gu", english: "basketball", usage: "Basketball" },
            { korean: "테니스", roman: "te-ni-seu", english: "tennis", usage: "Tennis" },
            { korean: "수영", roman: "su-yeong", english: "swimming", usage: "Swimming" },
            { korean: "자전거타기", roman: "ja-jeon-geo-ta-gi", english: "cycling", usage: "Bike riding" },
            { korean: "등산", roman: "deung-san", english: "hiking", usage: "Mountain climbing" },
            { korean: "요가", roman: "yo-ga", english: "yoga", usage: "Yoga practice" },
            { korean: "춤", roman: "chum", english: "dancing", usage: "Dance" }
          ]
        },
        {
          title: "Expressing Preferences",
          content: "Talking about likes and dislikes:",
          items: [
            { korean: "좋아해요", roman: "jo-a-hae-yo", english: "I like", usage: "Expressing preference" },
            { korean: "싫어해요", roman: "sil-eo-hae-yo", english: "I dislike", usage: "Expressing dislike" },
            { korean: "관심있어요", roman: "gwan-sim-it-seo-yo", english: "I'm interested", usage: "Expressing interest" },
            { korean: "재미있어요", roman: "jae-mi-it-seo-yo", english: "It's fun", usage: "Expressing enjoyment" },
            { korean: "지루해요", roman: "ji-ru-hae-yo", english: "It's boring", usage: "Expressing boredom" },
            { korean: "어려워요", roman: "eo-ryeo-wo-yo", english: "It's difficult", usage: "Expressing difficulty" },
            { korean: "쉬워요", roman: "swi-wo-yo", english: "It's easy", usage: "Expressing ease" }
          ]
        }
      ],
      
      practice: {
        title: "Hobby Practice",
        instructions: "Practice talking about hobbies:",
        scenarios: [
          { situation: "Asking about someone's hobby", answer: "취미가 뭐예요?" },
          { situation: "Saying you like reading", answer: "독서를 좋아해요" },
          { situation: "Saying you're interested in cooking", answer: "요리에 관심있어요" },
          { situation: "Saying soccer is fun", answer: "축구가 재미있어요" }
        ]
      },
      
      tips: [
        "Korean people often ask about hobbies as conversation starters",
        "Learn to ask follow-up questions about hobbies",
        "Practice describing why you like certain activities",
        "Be prepared to share your own hobbies when asked"
      ]
    }
  },
  
  intermediate7: {
    title: "Work & Career",
    difficulty: "Intermediate",
    duration: "45 min",
    description: "Discuss work, career, and professional life",
    icon: Award,
    content: {
      introduction: "Work and career are important topics in Korean society. This lesson covers job-related vocabulary and professional communication.",
      
      sections: [
        {
          title: "Job Titles",
          content: "Common job titles:",
          items: [
            { korean: "직업", roman: "jik-eop", english: "job/profession", usage: "General term for work" },
            { korean: "회사원", roman: "hoe-sa-won", english: "office worker", usage: "General office employee" },
            { korean: "선생님", roman: "seon-saeng-nim", english: "teacher", usage: "Educator" },
            { korean: "의사", roman: "ui-sa", english: "doctor", usage: "Medical professional" },
            { korean: "변호사", roman: "byeon-ho-sa", english: "lawyer", usage: "Legal professional" },
            { korean: "엔지니어", roman: "en-ji-ni-eo", english: "engineer", usage: "Technical professional" },
            { korean: "요리사", roman: "yo-ri-sa", english: "chef", usage: "Food professional" },
            { korean: "예술가", roman: "ye-sul-ga", english: "artist", usage: "Creative professional" }
          ]
        },
        {
          title: "Workplace Vocabulary",
          content: "Office and work-related terms:",
          items: [
            { korean: "회사", roman: "hoe-sa", english: "company", usage: "Workplace" },
            { korean: "사무실", roman: "sa-mu-sil", english: "office", usage: "Work space" },
            { korean: "회의", roman: "hoe-ui", english: "meeting", usage: "Business meeting" },
            { korean: "프로젝트", roman: "peu-ro-jek-teu", english: "project", usage: "Work assignment" },
            { korean: "업무", roman: "eop-mu", english: "work/task", usage: "Job duties" },
            { korean: "동료", roman: "dong-ryo", english: "colleague", usage: "Work partner" },
            { korean: "상사", roman: "sang-sa", english: "boss", usage: "Supervisor" },
            { korean: "부하직원", roman: "bu-ha-jik-won", english: "subordinate", usage: "Employee under you" }
          ]
        },
        {
          title: "Career Goals",
          content: "Talking about career aspirations:",
          items: [
            { korean: "목표", roman: "mok-pyo", english: "goal", usage: "Career objective" },
            { korean: "꿈", roman: "kkum", english: "dream", usage: "Career aspiration" },
            { korean: "성공", roman: "seong-gong", english: "success", usage: "Achievement" },
            { korean: "도전", roman: "do-jeon", english: "challenge", usage: "Difficult task" },
            { korean: "기회", roman: "gi-hoe", english: "opportunity", usage: "Chance for advancement" },
            { korean: "경험", roman: "gyeong-heom", english: "experience", usage: "Work background" },
            { korean: "능력", roman: "neung-ryeok", english: "ability", usage: "Skill or talent" },
            { korean: "자격증", roman: "ja-gyeok-jeung", english: "certificate", usage: "Professional qualification" }
          ]
        }
      ],
      
      practice: {
        title: "Work Practice",
        instructions: "Practice these work-related conversations:",
        scenarios: [
          { situation: "Asking about someone's job", answer: "직업이 뭐예요?" },
          { situation: "Saying you work at a company", answer: "회사에서 일해요" },
          { situation: "Asking about work hours", answer: "몇 시까지 일해요?" },
          { situation: "Saying you like your job", answer: "일이 재미있어요" }
        ]
      },
      
      tips: [
        "Korean work culture values hierarchy and respect",
        "Learn to use appropriate honorifics in workplace settings",
        "Practice talking about your career goals and aspirations",
        "Understand Korean work-life balance concepts"
      ]
    }
  },
  
  intermediate8: {
    title: "Travel & Transportation",
    difficulty: "Intermediate",
    duration: "50 min",
    description: "Navigate travel situations and transportation",
    icon: Target,
    content: {
      introduction: "Travel vocabulary is essential for getting around Korea. This lesson covers transportation, accommodation, and travel-related situations.",
      
      sections: [
        {
          title: "Transportation",
          content: "Different modes of transportation:",
          items: [
            { korean: "교통수단", roman: "gyo-tong-su-dan", english: "transportation", usage: "General term for transport" },
            { korean: "지하철", roman: "ji-ha-cheol", english: "subway", usage: "Underground train" },
            { korean: "버스", roman: "beo-seu", english: "bus", usage: "Public bus" },
            { korean: "택시", roman: "taek-si", english: "taxi", usage: "Private car service" },
            { korean: "기차", roman: "gi-cha", english: "train", usage: "Railway train" },
            { korean: "비행기", roman: "bi-haeng-gi", english: "airplane", usage: "Aircraft" },
            { korean: "배", roman: "bae", english: "ship", usage: "Water vessel" },
            { korean: "자전거", roman: "ja-jeon-geo", english: "bicycle", usage: "Bike" }
          ]
        },
        {
          title: "Accommodation",
          content: "Places to stay:",
          items: [
            { korean: "숙박", roman: "suk-bak", english: "accommodation", usage: "General term for lodging" },
            { korean: "호텔", roman: "ho-tel", english: "hotel", usage: "Commercial lodging" },
            { korean: "펜션", roman: "pen-syeon", english: "pension", usage: "Small lodging" },
            { korean: "게스트하우스", roman: "ge-seu-teu-ha-u-seu", english: "guesthouse", usage: "Budget accommodation" },
            { korean: "모텔", roman: "mo-tel", english: "motel", usage: "Simple lodging" },
            { korean: "예약", roman: "ye-yak", english: "reservation", usage: "Booking" },
            { korean: "체크인", roman: "che-keu-in", english: "check-in", usage: "Arrival process" },
            { korean: "체크아웃", roman: "che-keu-a-ut", english: "check-out", usage: "Departure process" }
          ]
        },
        {
          title: "Travel Situations",
          content: "Common travel phrases:",
          items: [
            { korean: "여행", roman: "yeo-haeng", english: "travel", usage: "General travel term" },
            { korean: "관광", roman: "gwan-gwang", english: "sightseeing", usage: "Tourist activities" },
            { korean: "여권", roman: "yeo-gwon", english: "passport", usage: "Travel document" },
            { korean: "비자", roman: "bi-ja", english: "visa", usage: "Entry permit" },
            { korean: "티켓", roman: "ti-ket", english: "ticket", usage: "Travel pass" },
            { korean: "가방", roman: "ga-bang", english: "bag", usage: "Luggage" },
            { korean: "지도", roman: "ji-do", english: "map", usage: "Navigation aid" },
            { korean: "가이드", roman: "ga-i-deu", english: "guide", usage: "Tour guide" }
          ]
        }
      ],
      
      practice: {
        title: "Travel Practice",
        instructions: "Practice these travel scenarios:",
        scenarios: [
          { situation: "Asking how to get to the station", answer: "역에 어떻게 가요?" },
          { situation: "Asking for a hotel reservation", answer: "호텔 예약하고 싶어요" },
          { situation: "Asking about tourist attractions", answer: "관광지가 어디에 있어요?" },
          { situation: "Asking for directions to the airport", answer: "공항에 어떻게 가요?" }
        ]
      },
      
      tips: [
        "Korean public transportation is very efficient and affordable",
        "Learn to read Korean subway maps and bus routes",
        "Practice asking for help when lost",
        "Understand Korean travel etiquette and customs"
      ]
    }
  },
  
  advanced2: {
    title: "Business Korean",
    difficulty: "Advanced",
    duration: "55 min",
    description: "Professional communication and business etiquette",
    icon: Award,
    content: {
      introduction: "Business Korean requires formal language, proper etiquette, and understanding of Korean corporate culture. This lesson covers professional communication.",
      
      sections: [
        {
          title: "Business Greetings",
          content: "Formal business introductions:",
          items: [
            { korean: "안녕하십니까", roman: "an-nyeong-ha-sim-ni-kka", english: "Hello (very formal)", usage: "Highest level of politeness" },
            { korean: "처음 뵙겠습니다", roman: "cheo-eum boep-get-seum-ni-da", english: "Nice to meet you", usage: "First meeting" },
            { korean: "성함이 어떻게 되십니까?", roman: "seong-ham-i eo-tteo-ke doe-sim-ni-kka?", english: "What is your name?", usage: "Asking for name formally" },
            { korean: "어느 회사에서 근무하십니까?", roman: "eo-neu hoe-sa-e-seo geun-mu-ha-sim-ni-kka?", english: "Which company do you work for?", usage: "Asking about workplace" },
            { korean: "부서는 어디십니까?", roman: "bu-seo-neun eo-di-sim-ni-kka?", english: "Which department?", usage: "Asking about department" },
            { korean: "직책은 무엇입니까?", roman: "jik-chaek-eun mu-eot-im-ni-kka?", english: "What is your position?", usage: "Asking about job title" }
          ]
        },
        {
          title: "Meeting Language",
          content: "Business meeting expressions:",
          items: [
            { korean: "회의를 시작하겠습니다", roman: "hoe-ui-reul si-jak-ha-get-seum-ni-da", english: "Let's start the meeting", usage: "Opening a meeting" },
            { korean: "의견을 말씀해 주세요", roman: "ui-gyeon-eul mal-sseum-hae ju-se-yo", english: "Please share your opinion", usage: "Asking for input" },
            { korean: "동의하십니까?", roman: "dong-ui-ha-sim-ni-kka?", english: "Do you agree?", usage: "Seeking agreement" },
            { korean: "반대 의견이 있으신가요?", roman: "ban-dae ui-gyeon-i it-eu-sin-ga-yo?", english: "Is there any opposition?", usage: "Checking for objections" },
            { korean: "결론을 내리겠습니다", roman: "gyeol-lon-eul nae-ri-get-seum-ni-da", english: "Let's conclude", usage: "Ending discussion" },
            { korean: "다음 회의는 언제입니까?", roman: "da-eum hoe-ui-neun eon-je-im-ni-kka?", english: "When is the next meeting?", usage: "Scheduling follow-up" }
          ]
        },
        {
          title: "Professional Etiquette",
          content: "Business etiquette and formalities:",
          items: [
            { korean: "명함을 주고받다", roman: "myeong-ham-eul ju-go-bat-da", english: "exchange business cards", usage: "Business card etiquette" },
            { korean: "인사말을 나누다", roman: "in-sa-mal-eul na-nu-da", english: "exchange greetings", usage: "Formal greeting" },
            { korean: "예의를 지키다", roman: "ye-ui-reul ji-ki-da", english: "maintain etiquette", usage: "Proper behavior" },
            { korean: "존경을 표하다", roman: "jon-gyeong-eul pyo-ha-da", english: "show respect", usage: "Demonstrating respect" },
            { korean: "협력을 구하다", roman: "hyeop-ryeok-eul gu-ha-da", english: "seek cooperation", usage: "Requesting collaboration" },
            { korean: "신뢰를 쌓다", roman: "sin-roe-reul ssa-da", english: "build trust", usage: "Establishing relationships" }
          ]
        }
      ],
      
      practice: {
        title: "Business Practice",
        instructions: "Practice these business scenarios:",
        scenarios: [
          { situation: "Introducing yourself in a business meeting", answer: "안녕하십니까. 저는 OOO입니다. OOO 회사에서 근무하고 있습니다." },
          { situation: "Asking for someone's opinion", answer: "의견을 말씀해 주시겠습니까?" },
          { situation: "Proposing a solution", answer: "이렇게 하는 것이 어떨까요?" },
          { situation: "Ending a meeting politely", answer: "오늘 회의는 여기서 마치겠습니다." }
        ]
      },
      
      tips: [
        "Korean business culture values hierarchy and seniority",
        "Always use the highest level of politeness in business settings",
        "Learn proper bowing and greeting etiquette",
        "Understand the importance of building relationships in Korean business"
      ]
    }
  },
  
  advanced3: {
    title: "Korean Literature",
    difficulty: "Advanced",
    duration: "65 min",
    description: "Explore Korean literature and cultural texts",
    icon: BookOpen,
    content: {
      introduction: "Korean literature offers deep insights into Korean culture, history, and society. This lesson introduces classic and modern Korean literary works.",
      
      sections: [
        {
          title: "Classic Literature",
          content: "Traditional Korean literary works:",
          items: [
            { korean: "춘향전", roman: "chun-hyang-jeon", english: "The Tale of Chunhyang", usage: "Classic love story" },
            { korean: "심청전", roman: "sim-cheong-jeon", english: "The Tale of Simcheong", usage: "Filial piety story" },
            { korean: "흥부전", roman: "heung-bu-jeon", english: "The Tale of Heungbu", usage: "Brotherhood story" },
            { korean: "구운몽", roman: "gu-un-mong", english: "The Cloud Dream of the Nine", usage: "Classic novel" },
            { korean: "한글", roman: "han-geul", english: "Korean alphabet", usage: "Literary significance" },
            { korean: "시조", roman: "si-jo", english: "sijo poetry", usage: "Traditional poetry form" }
          ]
        },
        {
          title: "Modern Literature",
          content: "Contemporary Korean authors and works:",
          items: [
            { korean: "김소월", roman: "gim-so-wol", english: "Kim Sowol", usage: "Modern poet" },
            { korean: "윤동주", roman: "yun-dong-ju", english: "Yun Dong-ju", usage: "Resistance poet" },
            { korean: "박경리", roman: "bak-gyeong-ri", english: "Park Kyung-ni", usage: "Novelist" },
            { korean: "이문열", roman: "i-mun-yeol", english: "Lee Mun-yeol", usage: "Contemporary author" },
            { korean: "한강", roman: "han-gang", english: "Han Kang", usage: "Modern novelist" },
            { korean: "김영하", roman: "gim-yeong-ha", english: "Kim Young-ha", usage: "Contemporary writer" }
          ]
        },
        {
          title: "Literary Analysis",
          content: "Terms for discussing literature:",
          items: [
            { korean: "주제", roman: "ju-je", english: "theme", usage: "Main idea" },
            { korean: "인물", roman: "in-mul", english: "character", usage: "Literary character" },
            { korean: "배경", roman: "bae-gyeong", english: "setting", usage: "Story location" },
            { korean: "구성", roman: "gu-seong", english: "structure", usage: "Story organization" },
            { korean: "상징", roman: "sang-jing", english: "symbol", usage: "Symbolic meaning" },
            { korean: "의미", roman: "ui-mi", english: "meaning", usage: "Significance" },
            { korean: "감상", roman: "gam-sang", english: "appreciation", usage: "Literary appreciation" },
            { korean: "비평", roman: "bi-pyeong", english: "criticism", usage: "Literary criticism" }
          ]
        }
      ],
      
      practice: {
        title: "Literature Practice",
        instructions: "Practice discussing literature:",
        scenarios: [
          { situation: "Asking about favorite author", answer: "좋아하는 작가가 누구예요?" },
          { situation: "Discussing a book's theme", answer: "이 책의 주제는 무엇인가요?" },
          { situation: "Talking about character development", answer: "인물이 어떻게 변했나요?" },
          { situation: "Sharing your interpretation", answer: "제 생각에는..." }
        ]
      },
      
      tips: [
        "Korean literature reflects historical and cultural contexts",
        "Learn to appreciate different literary genres and styles",
        "Practice reading Korean texts to improve comprehension",
        "Understand the relationship between literature and Korean identity"
      ]
    }
  },
  
  advanced4: {
    title: "Idioms & Expressions",
    difficulty: "Advanced",
    duration: "50 min",
    description: "Learn Korean idioms and cultural expressions",
    icon: Lightbulb,
    content: {
      introduction: "Korean idioms and expressions reveal cultural values and ways of thinking. Learning these will help you understand Korean culture and communicate more naturally.",
      
      sections: [
        {
          title: "Common Idioms",
          content: "Frequently used Korean idioms:",
          items: [
            { korean: "가슴이 뛴다", roman: "ga-seum-i ttwinda", english: "heart is beating", usage: "Excited or nervous" },
            { korean: "눈이 높다", roman: "nun-i nopda", english: "eyes are high", usage: "Having high standards" },
            { korean: "귀가 가렵다", roman: "gwi-ga ga-ryeopda", english: "ears are itchy", usage: "Someone is talking about you" },
            { korean: "손이 크다", roman: "son-i keuda", english: "hands are big", usage: "Generous" },
            { korean: "입이 무겁다", roman: "ib-i mu-geopda", english: "mouth is heavy", usage: "Good at keeping secrets" },
            { korean: "발이 넓다", roman: "bal-i neolpda", english: "feet are wide", usage: "Having many connections" }
          ]
        },
        {
          title: "Cultural Expressions",
          content: "Expressions that reflect Korean culture:",
          items: [
            { korean: "정이 많다", roman: "jeong-i manta", english: "have much affection", usage: "Warm-hearted" },
            { korean: "체면을 세우다", roman: "che-myeon-eul se-uda", english: "build face", usage: "Maintain dignity" },
            { korean: "눈치를 보다", roman: "nun-chi-reul boda", english: "watch the eye", usage: "Read the situation" },
            { korean: "한마음이 되다", roman: "han-ma-eum-i doeda", english: "become one heart", usage: "Unite in purpose" },
            { korean: "마음을 나누다", roman: "ma-eum-eul na-nuda", english: "share hearts", usage: "Share feelings" },
            { korean: "정을 나누다", roman: "jeong-eul na-nuda", english: "share affection", usage: "Build relationships" }
          ]
        },
        {
          title: "Proverbs",
          content: "Traditional Korean proverbs:",
          items: [
            { korean: "가는 말이 고와야 오는 말이 곱다", roman: "ga-neun mal-i go-wa-ya o-neun mal-i gopda", english: "If outgoing words are nice, incoming words will be nice", usage: "Treat others as you want to be treated" },
            { korean: "백지장도 맞들면 낫다", roman: "baek-ji-jang-do mat-deul-myeon natda", english: "Even a piece of paper is better when held together", usage: "Unity is strength" },
            { korean: "가는 날이 장날", roman: "ga-neun nal-i jang-nal", english: "The day you go is market day", usage: "Bad timing" },
            { korean: "호미로 막을 것을 가래로 막는다", roman: "ho-mi-ro ma-geul geot-eul ga-rae-ro mak-neunda", english: "Stop with a hoe what should be stopped with a shovel", usage: "Overkill" },
            { korean: "낙숫물이 댓돌을 뚫는다", roman: "nak-sut-mul-i daet-dol-eul ttul-neunda", english: "Dripping water pierces stone", usage: "Persistence pays off" },
            { korean: "원숭이도 나무에서 떨어진다", roman: "won-sung-i-do na-mu-e-seo tteol-eo-jinda", english: "Even monkeys fall from trees", usage: "Everyone makes mistakes" }
          ]
        }
      ],
      
      practice: {
        title: "Idiom Practice",
        instructions: "Practice using these expressions:",
        scenarios: [
          { situation: "Someone is very generous", answer: "손이 크시네요" },
          { situation: "Someone can keep secrets", answer: "입이 무겁으시네요" },
          { situation: "Someone has many connections", answer: "발이 넓으시네요" },
          { situation: "Encouraging someone to persist", answer: "낙숫물이 댓돌을 뚫는다고 하잖아요" }
        ]
      },
      
      tips: [
        "Idioms often reflect Korean cultural values and beliefs",
        "Learn the cultural context behind each expression",
        "Practice using idioms in appropriate situations",
        "Don't overuse idioms - use them naturally"
      ]
    }
  },
  
  intermediate7: {
    title: "Travel & Tourism",
    difficulty: "Intermediate",
    duration: "50 min",
    description: "Essential Korean for travelers",
    icon: Target,
    content: {
      introduction: "Traveling in Korea requires specific vocabulary and cultural knowledge. This lesson covers essential travel phrases, accommodation, and tourist situations.",
      
      sections: [
        {
          title: "Transportation",
          content: "Different modes of transportation:",
          items: [
            { korean: "교통수단", roman: "gyo-tong-su-dan", english: "transportation", usage: "General term for transport" },
            { korean: "지하철", roman: "ji-ha-cheol", english: "subway", usage: "Underground train" },
            { korean: "버스", roman: "beo-seu", english: "bus", usage: "Public bus" },
            { korean: "택시", roman: "taek-si", english: "taxi", usage: "Private car service" },
            { korean: "기차", roman: "gi-cha", english: "train", usage: "Railway train" },
            { korean: "비행기", roman: "bi-haeng-gi", english: "airplane", usage: "Aircraft" },
            { korean: "배", roman: "bae", english: "ship", usage: "Water vessel" },
            { korean: "자전거", roman: "ja-jeon-geo", english: "bicycle", usage: "Bike" }
          ]
        },
        {
          title: "Accommodation",
          content: "Places to stay:",
          items: [
            { korean: "숙박", roman: "suk-bak", english: "accommodation", usage: "General term for lodging" },
            { korean: "호텔", roman: "ho-tel", english: "hotel", usage: "Commercial lodging" },
            { korean: "펜션", roman: "pen-syeon", english: "pension", usage: "Small lodging" },
            { korean: "게스트하우스", roman: "ge-seu-teu-ha-u-seu", english: "guesthouse", usage: "Budget accommodation" },
            { korean: "모텔", roman: "mo-tel", english: "motel", usage: "Simple lodging" },
            { korean: "예약", roman: "ye-yak", english: "reservation", usage: "Booking" },
            { korean: "체크인", roman: "che-keu-in", english: "check-in", usage: "Arrival process" },
            { korean: "체크아웃", roman: "che-keu-a-ut", english: "check-out", usage: "Departure process" }
          ]
        }
      ],
      
      practice: {
        title: "Travel Practice",
        instructions: "Practice these travel scenarios:",
        scenarios: [
          { situation: "Book a hotel room", answer: "호텔 방을 예약하고 싶어요" },
          { situation: "Ask for directions to tourist attractions", answer: "관광지로 가는 길을 알려주세요" },
          { situation: "Ask about transportation options", answer: "교통수단이 뭐가 있나요?" },
          { situation: "Check in at a hotel", answer: "체크인하고 싶어요" }
        ]
      },
      
      tips: [
        "Korean tourism infrastructure is excellent",
        "Learn to use Korean travel apps like KakaoMap",
        "Practice polite language when asking for help",
        "Familiarize yourself with Korean cultural sites"
      ]
    }
  },
  
  intermediate8: {
    title: "Health & Medical",
    difficulty: "Intermediate",
    duration: "55 min",
    description: "Medical vocabulary and health conversations",
    icon: Target,
    content: {
      introduction: "Health and medical vocabulary is essential for living in Korea. This lesson covers body parts, symptoms, and medical situations.",
      
      sections: [
        {
          title: "Body Parts",
          content: "Essential body part vocabulary:",
          items: [
            { korean: "머리", roman: "meo-ri", english: "head", usage: "Body part" },
            { korean: "목", roman: "mok", english: "neck", usage: "Body part" },
            { korean: "어깨", roman: "eo-kkae", english: "shoulder", usage: "Body part" },
            { korean: "팔", roman: "pal", english: "arm", usage: "Body part" },
            { korean: "손", roman: "son", english: "hand", usage: "Body part" },
            { korean: "가슴", roman: "ga-seum", english: "chest", usage: "Body part" },
            { korean: "배", roman: "bae", english: "stomach", usage: "Body part" },
            { korean: "다리", roman: "da-ri", english: "leg", usage: "Body part" }
          ]
        },
        {
          title: "Symptoms & Conditions",
          content: "Common health issues:",
          items: [
            { korean: "아프다", roman: "a-peu-da", english: "to hurt/be sick", usage: "General pain" },
            { korean: "두통", roman: "du-tong", english: "headache", usage: "Head pain" },
            { korean: "열", roman: "yeol", english: "fever", usage: "High temperature" },
            { korean: "기침", roman: "gi-chim", english: "cough", usage: "Coughing" },
            { korean: "감기", roman: "gam-gi", english: "cold", usage: "Common cold" },
            { korean: "복통", roman: "bok-tong", english: "stomachache", usage: "Stomach pain" },
            { korean: "피곤하다", roman: "pi-gon-ha-da", english: "to be tired", usage: "Fatigue" },
            { korean: "어지럽다", roman: "eo-ji-reop-da", english: "to be dizzy", usage: "Dizziness" }
          ]
        }
      ],
      
      practice: {
        title: "Medical Practice",
        instructions: "Practice these medical scenarios:",
        scenarios: [
          { situation: "Describe a headache", answer: "머리가 아파요" },
          { situation: "Ask for medicine", answer: "약을 사고 싶어요" },
          { situation: "Make a doctor appointment", answer: "의사 선생님과 예약하고 싶어요" },
          { situation: "Describe symptoms", answer: "열이 나고 기침이 나요" }
        ]
      },
      
      tips: [
        "Korean healthcare is excellent and affordable",
        "Learn to describe symptoms clearly",
        "Practice polite language when talking to medical staff",
        "Familiarize yourself with Korean medical terms"
      ]
    }
  },
  
  intermediate9: {
    title: "Technology & Internet",
    difficulty: "Intermediate",
    duration: "45 min",
    description: "Modern tech vocabulary and digital communication",
    icon: Target,
    content: {
      introduction: "Technology vocabulary is essential in modern Korea. This lesson covers computers, internet, and digital communication terms.",
      
      sections: [
        {
          title: "Computer & Internet",
          content: "Basic technology terms:",
          items: [
            { korean: "컴퓨터", roman: "keom-pyu-teo", english: "computer", usage: "Personal computer" },
            { korean: "인터넷", roman: "in-teo-net", english: "internet", usage: "World wide web" },
            { korean: "웹사이트", roman: "wep-sa-i-teu", english: "website", usage: "Web page" },
            { korean: "이메일", roman: "i-me-il", english: "email", usage: "Electronic mail" },
            { korean: "파일", roman: "pa-il", english: "file", usage: "Computer file" },
            { korean: "폴더", roman: "pol-deo", english: "folder", usage: "File directory" },
            { korean: "프로그램", roman: "peu-ro-geu-raem", english: "program", usage: "Software program" },
            { korean: "소프트웨어", roman: "so-peu-teu-we-eo", english: "software", usage: "Computer software" }
          ]
        },
        {
          title: "Mobile & Communication",
          content: "Mobile technology terms:",
          items: [
            { korean: "스마트폰", roman: "seu-ma-teu-pon", english: "smartphone", usage: "Mobile phone" },
            { korean: "메시지", roman: "me-si-ji", english: "message", usage: "Text message" },
            { korean: "전화", roman: "jeon-hwa", english: "phone call", usage: "Telephone call" },
            { korean: "카메라", roman: "ka-me-ra", english: "camera", usage: "Photo camera" },
            { korean: "사진", roman: "sa-jin", english: "photo", usage: "Picture" },
            { korean: "동영상", roman: "dong-yeong-sang", english: "video", usage: "Moving picture" },
            { korean: "앱", roman: "aep", english: "app", usage: "Mobile application" },
            { korean: "다운로드", roman: "da-un-ro-deu", english: "download", usage: "Get from internet" }
          ]
        }
      ],
      
      practice: {
        title: "Technology Practice",
        instructions: "Practice these tech scenarios:",
        scenarios: [
          { situation: "Ask for WiFi password", answer: "와이파이 비밀번호가 뭐예요?" },
          { situation: "Ask to use computer", answer: "컴퓨터 사용해도 될까요?" },
          { situation: "Ask about phone charging", answer: "휴대폰 충전할 수 있어요?" },
          { situation: "Ask for help with app", answer: "이 앱 사용법을 알려주세요" }
        ]
      },
      
      tips: [
        "Korea has excellent internet infrastructure",
        "Learn to use Korean tech terms naturally",
        "Practice asking for tech help politely",
        "Familiarize yourself with Korean apps and services"
      ]
    }
  },
  
  advanced6: {
    title: "Korean Literature",
    difficulty: "Advanced",
    duration: "75 min",
    description: "Explore Korean literature and cultural expressions",
    icon: Target,
    content: {
      introduction: "Korean literature offers deep insights into Korean culture and language. This lesson explores literary forms, famous works, and cultural expressions.",
      
      sections: [
        {
          title: "Literary Forms",
          content: "Different types of Korean literature:",
          items: [
            { korean: "시", roman: "si", english: "poetry", usage: "Poetic form" },
            { korean: "소설", roman: "so-seol", english: "novel", usage: "Fictional story" },
            { korean: "수필", roman: "su-pil", english: "essay", usage: "Personal writing" },
            { korean: "희곡", roman: "hui-gok", english: "play", usage: "Dramatic work" },
            { korean: "전설", roman: "jeon-seol", english: "legend", usage: "Traditional story" },
            { korean: "민담", roman: "min-dam", english: "folktale", usage: "Folk story" },
            { korean: "동화", roman: "dong-hwa", english: "fairy tale", usage: "Children's story" },
            { korean: "자서전", roman: "ja-seo-jeon", english: "autobiography", usage: "Life story" }
          ]
        },
        {
          title: "Literary Terms",
          content: "Important literary vocabulary:",
          items: [
            { korean: "작가", roman: "jak-ga", english: "author", usage: "Writer" },
            { korean: "시인", roman: "si-in", english: "poet", usage: "Poetry writer" },
            { korean: "소설가", roman: "so-seol-ga", english: "novelist", usage: "Novel writer" },
            { korean: "문학", roman: "mun-hak", english: "literature", usage: "Literary works" },
            { korean: "작품", roman: "jak-pum", english: "work", usage: "Literary piece" },
            { korean: "주제", roman: "ju-je", english: "theme", usage: "Main idea" },
            { korean: "등장인물", roman: "deung-jang-in-mul", english: "character", usage: "Story person" },
            { korean: "줄거리", roman: "jul-geo-ri", english: "plot", usage: "Story outline" }
          ]
        }
      ],
      
      practice: {
        title: "Literature Practice",
        instructions: "Practice these literary scenarios:",
        scenarios: [
          { situation: "Discuss favorite book", answer: "좋아하는 책이 뭐예요?" },
          { situation: "Ask about author", answer: "이 작가에 대해 알려주세요" },
          { situation: "Describe story theme", answer: "이 소설의 주제는 무엇인가요?" },
          { situation: "Recommend literature", answer: "한국 문학을 추천해주세요" }
        ]
      },
      
      tips: [
        "Korean literature reflects deep cultural values",
        "Start with modern Korean authors for easier reading",
        "Learn literary vocabulary gradually",
        "Read both original and translated works"
      ]
    }
  },
  
  advanced7: {
    title: "Academic Korean",
    difficulty: "Advanced",
    duration: "80 min",
    description: "Formal academic writing and presentations",
    icon: Target,
    content: {
      introduction: "Academic Korean requires formal language and specialized vocabulary. This lesson covers academic writing, presentations, and scholarly communication.",
      
      sections: [
        {
          title: "Academic Writing",
          content: "Formal writing vocabulary:",
          items: [
            { korean: "논문", roman: "non-mun", english: "thesis", usage: "Academic paper" },
            { korean: "연구", roman: "yeon-gu", english: "research", usage: "Academic study" },
            { korean: "분석", roman: "bun-seok", english: "analysis", usage: "Detailed examination" },
            { korean: "결론", roman: "gyeol-lon", english: "conclusion", usage: "Final result" },
            { korean: "가설", roman: "ga-seol", english: "hypothesis", usage: "Theory to test" },
            { korean: "실험", roman: "sil-heom", english: "experiment", usage: "Scientific test" },
            { korean: "데이터", roman: "de-i-teo", english: "data", usage: "Information" },
            { korean: "통계", roman: "tong-gye", english: "statistics", usage: "Numerical data" }
          ]
        },
        {
          title: "Presentations",
          content: "Presentation vocabulary:",
          items: [
            { korean: "발표", roman: "bal-pyo", english: "presentation", usage: "Formal talk" },
            { korean: "강의", roman: "gang-ui", english: "lecture", usage: "Academic talk" },
            { korean: "세미나", roman: "se-mi-na", english: "seminar", usage: "Academic meeting" },
            { korean: "토론", roman: "to-ron", english: "discussion", usage: "Academic debate" },
            { korean: "질문", roman: "jil-mun", english: "question", usage: "Academic inquiry" },
            { korean: "답변", roman: "dap-byeon", english: "answer", usage: "Response" },
            { korean: "의견", roman: "ui-gyeon", english: "opinion", usage: "Personal view" },
            { korean: "논의", roman: "non-ui", english: "discussion", usage: "Academic debate" }
          ]
        }
      ],
      
      practice: {
        title: "Academic Practice",
        instructions: "Practice these academic scenarios:",
        scenarios: [
          { situation: "Present research findings", answer: "연구 결과를 발표하겠습니다" },
          { situation: "Ask academic question", answer: "질문이 있습니다" },
          { situation: "Discuss methodology", answer: "연구 방법에 대해 논의해보겠습니다" },
          { situation: "Conclude presentation", answer: "결론적으로 말씀드리면..." }
        ]
      },
      
      tips: [
        "Academic Korean uses very formal language",
        "Learn specialized vocabulary for your field",
        "Practice formal presentation skills",
        "Understand Korean academic culture and expectations"
      ]
    }
  },
  
  advanced8: {
    title: "Korean Media & News",
    difficulty: "Advanced",
    duration: "65 min",
    description: "Understand Korean news and media language",
    icon: Target,
    content: {
      introduction: "Korean media language is formal and uses specific vocabulary. This lesson covers news terminology, media expressions, and current events vocabulary.",
      
      sections: [
        {
          title: "News & Media",
          content: "Media-related vocabulary:",
          items: [
            { korean: "뉴스", roman: "nyu-seu", english: "news", usage: "Current events" },
            { korean: "보도", roman: "bo-do", english: "report", usage: "News report" },
            { korean: "기자", roman: "gi-ja", english: "reporter", usage: "News writer" },
            { korean: "방송", roman: "bang-song", english: "broadcast", usage: "TV/radio program" },
            { korean: "언론", roman: "eon-ron", english: "media", usage: "Press/media" },
            { korean: "신문", roman: "sin-mun", english: "newspaper", usage: "Print news" },
            { korean: "잡지", roman: "jap-ji", english: "magazine", usage: "Periodical" },
            { korean: "인터뷰", roman: "in-teo-byu", english: "interview", usage: "Question session" }
          ]
        },
        {
          title: "Current Events",
          content: "News and event vocabulary:",
          items: [
            { korean: "사건", roman: "sa-geon", english: "incident", usage: "News event" },
            { korean: "사고", roman: "sa-go", english: "accident", usage: "Unfortunate event" },
            { korean: "정치", roman: "jeong-chi", english: "politics", usage: "Government affairs" },
            { korean: "경제", roman: "gyeong-je", english: "economy", usage: "Financial matters" },
            { korean: "사회", roman: "sa-hoe", english: "society", usage: "Social issues" },
            { korean: "문화", roman: "mun-hwa", english: "culture", usage: "Cultural events" },
            { korean: "스포츠", roman: "seu-po-cheu", english: "sports", usage: "Athletic events" },
            { korean: "날씨", roman: "nal-ssi", english: "weather", usage: "Weather report" }
          ]
        }
      ],
      
      practice: {
        title: "Media Practice",
        instructions: "Practice these media scenarios:",
        scenarios: [
          { situation: "Discuss news article", answer: "이 뉴스 기사를 어떻게 생각하세요?" },
          { situation: "Ask about current events", answer: "최근 뉴스에 대해 어떻게 생각하세요?" },
          { situation: "Comment on political news", answer: "정치 뉴스에 대한 의견이 있나요?" },
          { situation: "Discuss economic news", answer: "경제 상황이 어떤가요?" }
        ]
      },
      
      tips: [
        "Korean news uses formal, objective language",
        "Learn to distinguish between different news sources",
        "Practice discussing current events politely",
        "Understand Korean media culture and perspectives"
      ]
    }
  },
  
  advanced5: {
    title: "Cultural Context",
    difficulty: "Advanced",
    duration: "75 min",
    description: "Understand Korean culture through language",
    icon: Target,
    content: {
      introduction: "Understanding Korean culture is essential for advanced language learning. This lesson explores cultural expressions, traditions, and social contexts.",
      
      sections: [
        {
          title: "Cultural Expressions",
          content: "Culture-specific vocabulary:",
          items: [
            { korean: "전통", roman: "jeon-tong", english: "tradition", usage: "Cultural heritage" },
            { korean: "예의", roman: "ye-ui", english: "etiquette", usage: "Proper behavior" },
            { korean: "정", roman: "jeong", english: "emotional bond", usage: "Korean concept of connection" },
            { korean: "한", roman: "han", english: "sorrow", usage: "Korean emotional concept" },
            { korean: "체면", roman: "che-myeon", english: "face", usage: "Social reputation" },
            { korean: "인맥", roman: "in-maek", english: "connections", usage: "Social network" },
            { korean: "정치", roman: "jeong-chi", english: "politics", usage: "Government affairs" },
            { korean: "사회", roman: "sa-hoe", english: "society", usage: "Social structure" }
          ]
        },
        {
          title: "Social Context",
          content: "Social relationship vocabulary:",
          items: [
            { korean: "관계", roman: "gwan-gye", english: "relationship", usage: "Social connection" },
            { korean: "의무", roman: "ui-mu", english: "duty", usage: "Social obligation" },
            { korean: "책임", roman: "chaek-im", english: "responsibility", usage: "Social duty" },
            { korean: "존경", roman: "jon-gyeong", english: "respect", usage: "Honor for others" },
            { korean: "배려", roman: "bae-ryeo", english: "consideration", usage: "Thoughtful care" },
            { korean: "협력", roman: "hyeom-nyeok", english: "cooperation", usage: "Working together" },
            { korean: "화합", roman: "hwa-hap", english: "harmony", usage: "Peaceful unity" },
            { korean: "공동체", roman: "gong-dong-che", english: "community", usage: "Group identity" }
          ]
        }
      ],
      
      practice: {
        title: "Cultural Practice",
        instructions: "Practice these cultural scenarios:",
        scenarios: [
          { situation: "Discuss Korean traditions", answer: "한국 전통에 대해 어떻게 생각하세요?" },
          { situation: "Talk about social relationships", answer: "한국 사회의 관계는 어떤가요?" },
          { situation: "Discuss cultural values", answer: "한국 문화의 가치는 무엇인가요?" },
          { situation: "Explain cultural concept", answer: "한국 문화의 특별한 점은 무엇인가요?" }
        ]
      },
      
      tips: [
        "Korean culture emphasizes harmony and respect",
        "Learn to understand cultural context in language",
        "Practice discussing cultural topics respectfully",
        "Study Korean history to understand cultural expressions"
      ]
    }
  }
};

function LessonPage() {
  const { id } = useParams();
  const [completed, setCompleted] = useState(false);
  const [showAnswer, setShowAnswer] = useState({});
  const lesson = lessons[id];

  useEffect(() => {
    const fetchProgress = async () => {
      const uid = auth.currentUser?.uid;
      if (uid) {
        const userRef = doc(db, "users", uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          setCompleted(data.completedLessons?.includes(id));
        }
      }
    };
    fetchProgress();
  }, [id]);

  const handleComplete = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const userRef = doc(db, "users", uid);
    const snap = await getDoc(userRef);
    let completedLessons = [];

    if (snap.exists()) {
      completedLessons = snap.data().completedLessons || [];
    }

    if (!completedLessons.includes(id)) {
      completedLessons.push(id);
      await updateDoc(userRef, { completedLessons });
      setCompleted(true);
    }
  };

  const toggleAnswer = (index) => {
    setShowAnswer(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (!lesson) return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Lesson Not Found</h1>
          <p className="text-gray-600">The lesson you're looking for doesn't exist.</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Lesson Header */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 shadow-2xl border-b-4 border-red-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-0">
                <div className="p-3 bg-gradient-to-r from-red-100 to-orange-100 rounded-xl border-2 border-red-200">
                  <lesson.icon className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{lesson.title}</h1>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
                      <Award className="w-4 h-4 mr-1" />
                      {lesson.difficulty}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-red-100 to-orange-100 text-red-800 border border-red-200">
                      <Clock className="w-4 h-4 mr-1" />
                      {lesson.duration}
                    </span>
                  </div>
                </div>
              </div>
          {!completed ? (
            <button
              onClick={handleComplete}
              className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-red-700 hover:to-orange-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
            >
              <CheckCircle className="w-5 h-5" />
              <span>Mark Complete</span>
            </button>
          ) : (
            <div className="w-full sm:w-auto flex items-center justify-center sm:justify-start space-x-2 text-green-600">
              <CheckCircle className="w-6 h-6" />
              <span className="font-semibold text-lg">Completed!</span>
            </div>
          )}
            </div>
            <p className="text-xl text-gray-600">{lesson.description}</p>
          </div>
        </div>

        {/* Lesson Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Introduction */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-lg p-8 mb-8 border-4 border-red-200">
            <div className="flex items-center mb-4">
              <Lightbulb className="w-6 h-6 text-amber-500 mr-2" />
              <h2 className="text-2xl font-bold text-gray-900">Introduction</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">{lesson.content.introduction}</p>
          </div>

          {/* Lesson Sections */}
          {lesson.content.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl shadow-lg p-8 mb-8 border-4 border-red-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h3>
              <p className="text-gray-700 mb-6">{section.content}</p>
              
              {/* Items or Examples */}
              {section.items && (
                <div className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-red-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-red-600 mb-2">{item.korean}</div>
                          <div className="text-sm text-gray-500">Korean</div>
                          <div className="mt-2">
                            <AudioPlayer word={item.korean} roman={item.roman} />
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-semibold text-gray-800 mb-2">{item.roman}</div>
                          <div className="text-sm text-gray-500">Pronunciation</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-medium text-gray-700 mb-2">{item.english}</div>
                          <div className="text-sm text-gray-500">English</div>
                        </div>
                      </div>
                      {item.usage && (
                        <div className="mt-4 p-3 bg-white rounded-lg border-l-4 border-red-400">
                          <p className="text-sm text-gray-600"><strong>Usage:</strong> {item.usage}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Examples */}
              {section.examples && (
                <div className="space-y-4">
                  {section.examples.map((example, exampleIndex) => (
                    <div key={exampleIndex} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600 mb-2">{example.korean}</div>
                          <div className="text-sm text-gray-500">Korean</div>
                          <div className="mt-2">
                            <AudioPlayer word={example.korean} roman={example.roman} />
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-semibold text-gray-800 mb-2">{example.roman}</div>
                          <div className="text-sm text-gray-500">Pronunciation</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-medium text-gray-700 mb-2">{example.english}</div>
                          <div className="text-sm text-gray-500">English</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Practice Section */}
          {lesson.content.practice && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 mb-8 border-4 border-green-200">
              <div className="flex items-center mb-6">
                <Target className="w-6 h-6 text-green-500 mr-2" />
                <h3 className="text-2xl font-bold text-gray-900">{lesson.content.practice.title}</h3>
              </div>
              <p className="text-gray-700 mb-6">{lesson.content.practice.instructions}</p>
              
              {/* Practice Words */}
              {lesson.content.practice.words && (
                <div className="space-y-4">
                  {lesson.content.practice.words.map((word, wordIndex) => (
                    <div key={wordIndex} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600 mb-2">{word.korean}</div>
                          <div className="text-sm text-gray-500">Korean</div>
                          <div className="mt-2">
                            <AudioPlayer word={word.korean} roman={word.roman} />
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-semibold text-gray-800 mb-2">{word.roman}</div>
                          <div className="text-sm text-gray-500">Pronunciation</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-medium text-gray-700 mb-2">{word.english}</div>
                          <div className="text-sm text-gray-500">English</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Practice Scenarios */}
              {lesson.content.practice.scenarios && (
                <div className="space-y-4">
                  {lesson.content.practice.scenarios.map((scenario, scenarioIndex) => (
                    <div key={scenarioIndex} className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-lg font-medium text-gray-800 mb-2">{scenario.situation}</p>
                          <button
                            onClick={() => toggleAnswer(scenarioIndex)}
                            className="text-red-600 hover:text-red-700 font-medium text-sm"
                          >
                            {showAnswer[scenarioIndex] ? 'Hide Answer' : 'Show Answer'}
                          </button>
                          {showAnswer[scenarioIndex] && (
                            <div className="mt-3 p-3 bg-white rounded-lg border-l-4 border-red-400">
                              <p className="text-lg font-semibold text-red-600">{scenario.answer}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Practice Example */}
              {lesson.content.practice.example && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">{lesson.content.practice.example.korean}</h4>
                  <div className="space-y-3">
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Pronunciation:</p>
                      <p className="text-lg font-medium text-gray-800">{lesson.content.practice.example.roman}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">English:</p>
                      <p className="text-lg font-medium text-gray-800">{lesson.content.practice.example.english}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tips Section */}
          {lesson.content.tips && (
            <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl shadow-lg p-8 text-white">
              <div className="flex items-center mb-6">
                <Lightbulb className="w-6 h-6 text-yellow-300 mr-2" />
                <h3 className="text-2xl font-bold">Study Tips</h3>
              </div>
              <div className="space-y-4">
                {lesson.content.tips.map((tip, tipIndex) => (
                  <div key={tipIndex} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-yellow-300 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-red-600 font-bold text-sm">{tipIndex + 1}</span>
                    </div>
                    <p className="text-red-100 leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default LessonPage;